/**
 * BASKETBALL DRILLS DATA STRUCTURE - CONSOLIDATED
 *
 * This file contains consolidated basketball drills organized into skill levels:
 * 1. Beginner Dribbling (30 minutes) - Basic ball handling and dribbling skills
 * 2. Intermediate Dribbling (45 minutes) - Advanced dribbling and ball control
 * 3. Advanced Dribbling (45 minutes) - Complex dribbling moves and combinations
 * 4. Beginner Shooting (30 minutes) - Basic shooting form and technique
 * 5. Intermediate Shooting (45 minutes) - Advanced shooting and accuracy
 * 6. Advanced Shooting (45 minutes) - Complex shooting scenarios and pressure
 * 7. Beginner Defense (30 minutes) - Basic defensive stance and movement
 * 8. Intermediate Defense (45 minutes) - Advanced defensive techniques
 * 9. Advanced Defense (45 minutes) - Complex defensive strategies
 * 10. Complete Individual Basketball (60 minutes) - Comprehensive basketball workout
 *
 * DRILL STRUCTURE:
 * - id: Numeric ID starting from 7001 (basketball drills)
 * - name: Clean drill name (no emojis - for TTS audio generation)
 * - categories: Array of skill categories
 * - sports: Array containing 'basketball'
 * - difficulty: 'beginner', 'intermediate', or 'advanced'
 * - skill: High-level skill category (e.g., 'Dribbling', 'Shooting', 'Defense')
 * - points: Point value for completion (30-60 points based on time)
 * - instructions: Detailed explanation including drill name introduction
 * - timedInstructions: Array of {time, text} objects for SSML generation
 * - timeTotal: Total duration in seconds (1800-3600 seconds = 30-60 minutes)
 * - equipment: Required equipment (e.g., 'Basketball' or 'Basketball and hoop')
 *
 * AUDIO GENERATION NOTES:
 * - Instructions field contains drill name introduction
 * - timedInstructions start directly with action steps
 * - SSML breaks are calculated based on time gaps between instructions
 * - Voice used: en-US-Studio-M (male Studio voice that supports SSML)
 *
 * BASKETBALL DRILL REQUIREMENTS:
 * - All drills doable at home/indoors with minimal space
 * - Focus on individual skills (no partner drills for safety)
 * - Equipment limited to basketball and optional hoop
 * - Appropriate for 6-10 year olds
 * - Focus on fundamental basketball skills and coordination
 * - Progressive skill development within each drill
 *
 * CREATED: [Date] - Basketball drills with SSML audio generation
 * UPDATED: [Date] - Consolidated into skill-based structure with longer drills
 */

const basketballDrills = [
  // BEGINNER DRIBBLING (30 minutes)
  {
    id: 2001,
    name: 'Beginner Dribbling Adventure',
    categories: ['Dribbling', 'beginner'],
    sports: ['basketball'],
    difficulty: 'beginner',
    skill: 'Dribbling',
    points: 15,
    instructions:
      "Get ready to become a basketball superstar! In this adventure, you'll learn to handle the ball like a pro. We're going to bounce, dribble, and have a ton of fun. Let's see if you have what it takes to complete the dribbling challenge! Grab your basketball and let's go!",
    timedInstructions: [
      {
        time: 0,
        text: "Hello basketball star! Get ready for a super fun dribbling adventure! Grab your ball, find some space, and let's start bouncing! The challenge begins now!",
      },
      {
        time: 15,
        text: "First, let's wake up the ball! Hold it in front of you with both hands and slap it back and forth, like you're giving it a high-five! Go fast! Slap, slap, slap!",
      },
      {
        time: 35,
        text: 'Faster! Keep going! Wake that ball up! Five, four, three, two, one!',
      },
      {
        time: 45,
        text: "Great! Now, let's pass the ball around our body! Start at your tummy, pass it around your back, and catch it in the front. Make a big circle! Don't drop it!",
      },
      {
        time: 75,
        text: 'Now switch! Go the other way! Try not to let the ball touch your body!',
      },
      {
        time: 105,
        text: "Awesome! Now stand with your feet together like a statue. We're going to circle the ball around both legs. Keep your legs straight! Go!",
      },
      {
        time: 140,
        text: "Keep going! Don't stop moving! You're a ball-handling wizard!",
      },
      {
        time: 155,
        text: "Okay, time to dribble! Let's start with your strong hand. Get low like you're sitting in a tiny chair, and pound the ball hard into the floor! Use your fingertips! Keep the bounce below your waist! Go!",
      },
      {
        time: 185,
        text: "Great! Keep bouncing on your feet! Let's do that again, even harder this time! Pound it! Go!",
      },
      {
        time: 215,
        text: "Amazing! Time for your other hand! It might feel a little silly, but that's how we get better! Get low and pound it with your other hand now! Go!",
      },
      {
        time: 245,
        text: 'One more time! Best one yet! Pound it hard! Go, go, go!',
      },
      {
        time: 275,
        text: "Whoo! Shake out your arms! Jog in place! Keep your feet moving, don't get stuck! You're doing awesome!",
      },
      {
        time: 295,
        text: "Ready for a cool move? The Crossover! We're going to bounce the ball in a V-shape from one hand to the other. Keep it low! Let's try it!",
      },
      {
        time: 330,
        text: "Nice! Keep those feet bouncing! Don't look at the ball, look up like a superhero!",
      },
      {
        time: 345,
        text: "Okay, let's speed it up! Quick crossovers! Go!",
      },
      {
        time: 375,
        text: "You're a crossover machine! Keep shuffling those feet!",
      },
      {
        time: 390,
        text: "Okay, this one is super cool! Put one foot forward. We're going to dribble the ball right between our legs to the other hand. Let's try it slowly. Just go through one leg!",
      },
      {
        time: 420,
        text: "Now switch your feet! Put the other foot forward! Let's try the other side!",
      },
      {
        time: 450,
        text: "Awesome work! Let's put it all together! The Figure 8! Spread your feet and dribble the ball in a number 8 pattern around and through your legs. Take your time!",
      },
      {
        time: 510,
        text: 'You got it! If you mess up, just start again! No problem! Keep trying!',
      },
      {
        time: 525,
        text: "Great job standing still! Now let's move! Let's just walk forward while dribbling with your strong hand. Four steps forward, then four steps backward. Go!",
      },
      {
        time: 565,
        text: 'Awesome! Now switch to your other hand! Walk and dribble. Four steps forward, four steps back. Keep your head up!',
      },
      {
        time: 605,
        text: 'Keep those feet light! Light on your toes!',
      },
      {
        time: 620,
        text: "Let's get fancy while we walk! Take a step and do a crossover. Another step, another crossover. Try to time it with your feet!",
      },
      {
        time: 670,
        text: "Looking good! Keep your eyes up, don't watch the ball!",
      },
      {
        time: 685,
        text: "Okay, now we're going to shuffle sideways, like a crab! Shuffle two times to the right while dribbling with your right hand. Then shuffle two times left, dribbling with your left hand! Go!",
      },
      {
        time: 735,
        text: 'Quick feet, quick feet! You are a superstar!',
      },
      {
        time: 750,
        text: "It's time for the final Boss Battle! The Speed Challenge! I'm going to call out a move, and you do it as fast as you can! Get ready!",
      },
      {
        time: 765,
        text: 'First up... Right Hand POUNDS for twenty seconds! Go!',
      },
      {
        time: 785,
        text: 'Great! Now... Left Hand POUNDS for twenty seconds! Go!',
      },
      {
        time: 805,
        text: "Ready? Fast CROSSOVERS for twenty seconds! Let's see 'em!",
      },
      {
        time: 825,
        text: "Awesome! Now, LOW Dribbles with your Right Hand! Super low, like you're hiding the ball from a giant! Twenty seconds, Go!",
      },
      {
        time: 845,
        text: 'Last one! LOW Dribbles with your Left Hand! Finish strong! Twenty seconds, Go, go, go!',
      },
      {
        time: 865,
        text: 'Almost there! Five, four, three, two, one!',
      },
      {
        time: 870,
        text: "Wait, one more secret level! The Combo Breaker! We're going to do a special pattern. It goes: one, two, CROSSOVER! Then switch hands! one, two, CROSSOVER! Try it with me!",
      },
      {
        time: 915,
        text: "Keep the rhythm! One, two, cross! One, two, cross! You've got this!",
      },
      {
        time: 945,
        text: "And STOP! NOW you've really done it! You beat the Combo Breaker! You are an official Dribble Master! Give yourself a huge high five! Great job today!",
      },
    ],
    timeTotal: 960,
    equipment: 'Basketball',
  },

  // INTERMEDIATE DRIBBLING (45 minutes)
  {
    id: 2002,
    name: 'Intermediate Dribbling',
    categories: ['Dribbling', 'intermediate'],
    sports: ['basketball'],
    difficulty: 'intermediate',
    skill: 'Dribbling',
    points: 33,
    instructions:
      "Get ready to become a dribbling superstar! We're going to learn awesome moves that will make you look like a pro! We'll do super cool crossovers, go between our legs, and even behind the back! Let's grab our basketballs and get ready to have some fun!",
    timedInstructions: [
      {
        time: 0,
        text: "Get your basketball and find a nice open space! Let's get started in 3, 2, 1, GO!",
      },
      {
        time: 5,
        text: 'Start with a nice, easy bounce. Just get a feel for the ball. Keep your knees bent!',
      },
      {
        time: 15,
        text: "Okay, first challenge! Right hand only! We're doing POUND dribbles! I want you to smash that ball into the ground! As hard as you can! Go!",
      },
      {
        time: 30,
        text: 'Keep pounding it! Boom! Boom! Boom! Make the floor shake! Stay low like a tiger!',
      },
      {
        time: 45,
        text: "Awesome! Switch hands! Now it's the left hand's turn! Same thing, POUND that ball! Let's go!",
      },
      {
        time: 60,
        text: "Harder! Faster! Keep that power up! You're doing great! Keep bouncing!",
      },
      {
        time: 75,
        text: "Great job! Now let's try alternating! One big pound with the right, then one with the left! Back and forth! Boom, boom, boom!",
      },
      {
        time: 90,
        text: 'Keep it going! Keep your head up! Try to look at me, not the ball! Five more seconds!',
      },
      {
        time: 100,
        text: 'Awesome work! Shake it out! Now, for our first real move... the Crossover!',
      },
      {
        time: 105,
        text: "A crossover is when you bounce the ball in a V shape from one hand to the other. Let's try it slow. Right to left. Left to right. Make a nice, sharp V!",
      },
      {
        time: 120,
        text: "Keep the dribble low! Below your knees! That's the secret! Nice and controlled. Great job!",
      },
      {
        time: 135,
        text: "SPEED DRILL! Let's see how fast you can do your crossovers! Stay in one spot and go as fast as you can! Quick, quick, quick! Go, go, go!",
      },
      {
        time: 150,
        text: "Faster! Faster! Imagine you're tricking a defender! Keep those feet moving! Shuffle, shuffle!",
      },
      {
        time: 165,
        text: "Time! Wow, you're fast! Now let's try walking with the crossover. Take a step, and cross the ball over to your other hand. Easy does it.",
      },
      {
        time: 180,
        text: 'Take a few steps forward, then a few steps back. Always doing that V-shape crossover with every step. You got this!',
      },
      {
        time: 200,
        text: 'Keep your superhero guard up with your other hand! Protect the ball! Keep going!',
      },
      {
        time: 225,
        text: 'Fantastic! Stop right there. Next up, a super cool move... Between the Legs!',
      },
      {
        time: 235,
        text: 'Put your right foot forward, a little bit in front of your left. Now, try to bounce the ball from your right hand, UNDER your right leg, to your left hand!',
      },
      {
        time: 250,
        text: "It's tricky, I know! Just keep trying! Bounce it under that leg. Pop! You got it!",
      },
      {
        time: 265,
        text: 'Keep practicing on that same side! Right foot forward, bounce it under. Great job! Almost there!',
      },
      {
        time: 280,
        text: "Okay, let's switch! Now put your LEFT foot forward. Bounce the ball from your left hand, UNDER your left leg, to your right hand. Let's try it!",
      },
      {
        time: 300,
        text: 'Stay low, it makes it easier! Bend your knees! Keep your eyes up! Come on, you can do it!',
      },
      {
        time: 325,
        text: "Awesome! Now let's try walking with it. Take a step with your right foot, and dribble under the right leg. Then step with your left, and dribble under the left leg!",
      },
      {
        time: 340,
        text: "Step, under. Step, under. You're like a dribbling machine! Keep that rhythm!",
      },
      {
        time: 360,
        text: "COMBO TIME! Let's mix two moves together! We're going to do a Crossover, then right away, go Between the Legs!",
      },
      {
        time: 375,
        text: "So it goes: Crossover in front, then step and pop it between your legs! Let's start with the right hand. Cross over to the left, then go between the legs back to the right! Try it!",
      },
      {
        time: 395,
        text: "Cross... under! Cross... under! Find that rhythm! You're doing awesome! Keep your feet bouncing!",
      },
      {
        time: 420,
        text: "Okay, let's try starting with the other hand! Start with the ball in your left hand. Cross over to the right, then go between the legs back to the left!",
      },
      {
        time: 440,
        text: "Left hand cross... then under! Keep it going! Don't worry if you lose the ball, just pick it up and try again! That's how we get better!",
      },
      {
        time: 465,
        text: 'Ten more seconds of this combo! You are a rockstar! Go, go, go!',
      },
      {
        time: 480,
        text: 'Great work! Time for the ultimate cool move... Behind the Back!',
      },
      {
        time: 490,
        text: "This one feels like a magic trick. You're going to bounce the ball from one hand, around your back, to your other hand. Let's try it slow. No walking, just stand still.",
      },
      {
        time: 510,
        text: 'Wrap it around! Toss it from your right hand, behind you, and catch it with your left. Then try the other way!',
      },
      {
        time: 540,
        text: "Okay, let's try walking with it. Take a step, and as you do, wrap the ball behind your back to the other hand. Step and wrap! Step and wrap!",
      },
      {
        time: 570,
        text: "This move is tricky so just keep trying! You're doing great! Keep your feet moving, keep bouncing!",
      },
      {
        time: 600,
        text: 'SPEED DRILL! Stand still! How many Behind the Backs can you do in 30 seconds? Go as fast as you can! Go, go, go!',
      },
      {
        time: 615,
        text: "Quick, quick, quick! Wrap it around! Keep control! You're a blur!",
      },
      {
        time: 630,
        text: "Time! Woo! Are you ready for another combo? Let's do a Crossover, then go Behind the Back!",
      },
      {
        time: 645,
        text: 'Start with the ball in your right hand. Do a crossover to your left hand, then immediately wrap it behind your back to your right hand! Front V, then back wrap!',
      },
      {
        time: 670,
        text: "Cross... behind! Cross... behind! It's like you're dancing with the ball! Keep those feet shuffling left and right!",
      },
      {
        time: 690,
        text: "Now let's start with the left hand! Crossover to the right, then behind the back to the left! Let's see it!",
      },
      {
        time: 710,
        text: "Keep trying! This is a tough one! You're building super skills right now! Don't give up!",
      },
      {
        time: 735,
        text: 'Almost there! Five, four, three, two, one! YES! Amazing effort!',
      },
      {
        time: 750,
        text: 'Okay, new move! The In and Out dribble! This one looks super fancy. Keep the ball in your right hand.',
      },
      {
        time: 760,
        text: "You're going to pretend to do a crossover, but then pull the ball back! Your hand moves like a windshield wiper over the ball. In, then out! In, then out! Try it!",
      },
      {
        time: 780,
        text: 'Keep it all in your right hand. Fake the crossover, and pull it back. Swish, swish! You got it!',
      },
      {
        time: 810,
        text: 'Switch hands! Now try the In and Out with your left hand. Make the fake, then pull it back! Keep the ball on a string!',
      },
      {
        time: 840,
        text: 'Looking good! Keep practicing that windshield wiper motion! In, out, in, out! Stay low!',
      },
      {
        time: 870,
        text: 'Okay, this is it! The GRAND FINALE COMBO CHALLENGE! We are putting EVERYTHING together!',
      },
      {
        time: 880,
        text: "Listen carefully! The combo is: Crossover... then Between the Legs... then Behind the Back! Woah! Let's try it super slow at first.",
      },
      {
        time: 900,
        text: 'Start with your right hand. Crossover to the left. Then go between your legs back to the right. Then go behind your back to the left! Take your time.',
      },
      {
        time: 930,
        text: "Let's try that again. Cross... under... behind! Cross... under... behind! You can do this!",
      },
      {
        time: 960,
        text: "You are a dribbling master! This is the ultimate combo! Keep trying it! Don't stop bouncing!",
      },
      {
        time: 990,
        text: "FINAL SPEED DRILL! Let's do that whole combo, but faster! For the next minute, it's all you've got! Go as fast as you can!",
      },
      {
        time: 1005,
        text: 'Crossover, between the legs, behind the back! Go, go, go! If you mess up, just start the combo over! No problem!',
      },
      {
        time: 1030,
        text: "Come on, power through! You're almost at the finish line! Keep that ball moving! Keep your feet moving!",
      },
      {
        time: 1050,
        text: 'Ten seconds left! Give me your best ones! Five, four, three, two, one! STOP!',
      },
      {
        time: 1065,
        text: 'YOU DID IT! That was amazing! For the last few seconds, just do any moves you want! Show me your freestyle! Go crazy!',
      },
      {
        time: 1080,
        text: 'Great job today! You are an official dribbling superstar! Way to work hard!',
      },
    ],
    timeTotal: 1085,
    equipment: 'Basketball',
  },

  // ADVANCED DRIBBLING (45 minutes)
  {
    id: 2003,
    name: 'Advanced Dribbling',
    categories: ['Dribbling', 'advanced'],
    sports: ['basketball'],
    difficulty: 'advanced',
    skill: 'Dribbling',
    points: 25,
    instructions:
      "Ready to become a dribbling superstar? This game will teach you awesome moves like the Crossover, the Between-the-Legs, and even the super cool Behind-the-Back! Let's learn amazing tricks and become a basketball wizard in this super fun, non-stop dribbling adventure!",
    timedInstructions: [
      {
        time: 0,
        text: 'Get your basketball!',
      },
      {
        time: 2,
        text: "Let's become dribbling superstars!",
      },
      {
        time: 5,
        text: 'Start bouncing on your toes...',
      },
      {
        time: 8,
        text: 'Get ready... GO!',
      },
      {
        time: 11,
        text: "Okay! First, let's pound the ball!",
      },
      {
        time: 15,
        text: 'With your right hand, smack the ball down hard! Boom! Boom! Boom!',
      },
      {
        time: 21,
        text: 'Keep it below your knee! Faster! Faster!',
      },
      {
        time: 26,
        text: 'Keep your eyes up! Look at me, not the ball!',
      },
      {
        time: 30,
        text: 'Keep pounding it! You got this! Harder!',
      },
      {
        time: 35,
        text: 'Ten more seconds!',
      },
      {
        time: 45,
        text: 'Five, four, three, two, one!',
      },
      {
        time: 51,
        text: 'Great job! Switch hands!',
      },
      {
        time: 54,
        text: 'Now the left hand! Smack it down hard! Pound it!',
      },
      {
        time: 59,
        text: "Don't be shy! Faster now!",
      },
      {
        time: 63,
        text: 'Eyes up, superstar! You got this!',
      },
      {
        time: 68,
        text: 'Keep that power dribble going!',
      },
      {
        time: 73,
        text: 'Ten more seconds, almost there!',
      },
      {
        time: 83,
        text: 'Five, four, three, two, one!',
      },
      {
        time: 89,
        text: 'Whoa! Stop! Shake out your hands!',
      },
      {
        time: 93,
        text: "Wiggle your fingers! Keep bouncing on your feet! Don't stop moving!",
      },
      {
        time: 99,
        text: 'Okay, now for secret agent dribbles!',
      },
      {
        time: 103,
        text: 'Get low! Super low! Keep the ball right by your ankle.',
      },
      {
        time: 108,
        text: 'Tiny, fast dribbles with your right hand! Tap-tap-tap-tap!',
      },
      {
        time: 114,
        text: "Like you're typing on the basketball! Faster! Keep it low!",
      },
      {
        time: 120,
        text: "You're a secret agent, nobody can see your dribble! Keep going!",
      },
      {
        time: 127,
        text: 'Ten more seconds!',
      },
      {
        time: 137,
        text: 'Five, four, three, two, one!',
      },
      {
        time: 143,
        text: 'Switch! Left hand! Get low again!',
      },
      {
        time: 147,
        text: 'Tiny secret agent dribbles! Tap-tap-tap-tap!',
      },
      {
        time: 152,
        text: 'Keep it super close to your ankle! Nobody can steal it from you!',
      },
      {
        time: 158,
        text: 'Faster! Go, go, go!',
      },
      {
        time: 162,
        text: 'Ten more seconds on this side!',
      },
      {
        time: 172,
        text: 'Awesome! Stand up!',
      },
      {
        time: 175,
        text: 'Jog in place! Keep that ball bouncing with one hand while you jog!',
      },
      {
        time: 181,
        text: "Ready for a cool move? It's the Crossover!",
      },
      {
        time: 186,
        text: 'Dribble with one hand, then bounce it sideways in front of you to your other hand!',
      },
      {
        time: 192,
        text: 'Make a V-shape on the floor! Right to left! V! Left to right! V!',
      },
      {
        time: 200,
        text: "Now let's speed it up! Crossover, crossover, crossover!",
      },
      {
        time: 206,
        text: 'Keep the ball low and fast! Snap it from one hand to the other!',
      },
      {
        time: 212,
        text: 'Go! Go! Go! Ten more!',
      },
      {
        time: 218,
        text: 'One! Two! Three! Four! Five! Six! Seven! Eight! Nine! Ten!',
      },
      {
        time: 230,
        text: "You're a crossover machine! Okay, quick break! Keep shuffling your feet side to side!",
      },
      {
        time: 237,
        text: "Don't stop moving!",
      },
      {
        time: 240,
        text: 'Time for a super trick! The Between-the-Legs dribble!',
      },
      {
        time: 246,
        text: 'Stand with your right foot forward. Dribble the ball through the tunnel your legs make!',
      },
      {
        time: 252,
        text: 'Bounce it from your front hand to your back hand! Whoosh!',
      },
      {
        time: 257,
        text: "Again! And again! Keep trying! It's okay if you mess up!",
      },
      {
        time: 263,
        text: "Let's do five on this side! One... two... three... four... five!",
      },
      {
        time: 273,
        text: "Switch feet! Left foot forward now! Let's do the other side!",
      },
      {
        time: 279,
        text: "Dribble it through the leg tunnel! Whoosh! You're getting it!",
      },
      {
        time: 285,
        text: "Five more! Let's go! One... two... three... four... five!",
      },
      {
        time: 295,
        text: 'Amazing! Jog in place! Keep a nice, easy dribble going. Breathe!',
      },
      {
        time: 302,
        text: "Let's mix it up! We're going to do a double crossover!",
      },
      {
        time: 307,
        text: "It's two crossovers in a row! Right-to-left, then BAM, left-to-right!",
      },
      {
        time: 314,
        text: "Try it! One-two! Now let's drill it!",
      },
      {
        time: 319,
        text: 'Double crossover! Again! Faster! Make the ball dance!',
      },
      {
        time: 325,
        text: 'One-two! One-two! Keep going!',
      },
      {
        time: 330,
        text: 'Ten more seconds!',
      },
      {
        time: 340,
        text: 'Five, four, three, two, one! STOP!',
      },
      {
        time: 346,
        text: 'Okay, this is the ultimate cool move! The Behind-the-Back!',
      },
      {
        time: 352,
        text: "You're going to bounce the ball behind your body from one hand to the other!",
      },
      {
        time: 358,
        text: "Like you're passing it to a secret teammate behind you!",
      },
      {
        time: 363,
        text: "Let's try it! Start with the ball in your right hand. Swing your arm and bounce it to your left hand.",
      },
      {
        time: 370,
        text: "It's tricky, so take your time! Just try to tap it to the other side.",
      },
      {
        time: 376,
        text: "You can do it! Let's try five times! Don't worry if you lose the ball!",
      },
      {
        time: 383,
        text: 'One... Two... Three... Four... Five!',
      },
      {
        time: 393,
        text: 'Nice try! That move is tough! Shake it out! Keep bouncing on your toes!',
      },
      {
        time: 400,
        text: 'New combo! One crossover, then one between-the-legs!',
      },
      {
        time: 406,
        text: 'So you go side-to-side, then through the tunnel! Crossover... through the legs!',
      },
      {
        time: 413,
        text: "Crossover... through the legs! Let's get a rhythm! Go, go, go!",
      },
      {
        time: 420,
        text: 'Keep practicing that combo!',
      },
      {
        time: 426,
        text: 'SPEED DRILL! Ten crossovers! As fast as you can!',
      },
      {
        time: 432,
        text: 'Ready, set, GO! One, two, three, four, five, six, seven, eight, nine, ten!',
      },
      {
        time: 443,
        text: 'Awesome! Whoa! Fast feet! Shuffle side to side! Keep dribbling!',
      },
      {
        time: 450,
        text: "Let's see how high and low you can go!",
      },
      {
        time: 454,
        text: 'Right hand only! Dribble up to your shoulder! Now all the way down to your ankle!',
      },
      {
        time: 461,
        text: 'Shoulder! Ankle! Up! Down! Up! Down!',
      },
      {
        time: 468,
        text: 'Ten more seconds!',
      },
      {
        time: 478,
        text: 'Switch hands! Left hand now! All the way up to your shoulder!',
      },
      {
        time: 484,
        text: 'All the way down to your ankle! Keep control! Up! Down! Up! Down!',
      },
      {
        time: 492,
        text: 'Almost there! Five, four, three, two, one!',
      },
      {
        time: 498,
        text: "Time for a new trick! 'Around the World' but just one leg!",
      },
      {
        time: 504,
        text: 'Pick your right leg! Dribble the ball in a circle all the way around your right foot!',
      },
      {
        time: 511,
        text: "Keep it close! Let's do three circles! One... two... three!",
      },
      {
        time: 521,
        text: 'Great! Now circle your left leg!',
      },
      {
        time: 525,
        text: 'Keep dribbling in a perfect circle all the way around your left foot!',
      },
      {
        time: 531,
        text: "You're like a wizard with the ball! Three circles now! One... two... three!",
      },
      {
        time: 542,
        text: 'FINAL COMBO TIME!',
      },
      {
        time: 545,
        text: 'We are going to do: one crossover, one between the legs, then one behind the back!',
      },
      {
        time: 553,
        text: "Let's walk through it slowly. Crossover... good. Between the legs... nice. Behind the back... awesome!",
      },
      {
        time: 562,
        text: "Let's try to do that combo five times in a row! Take your time.",
      },
      {
        time: 568,
        text: "Ready? GO! That's one!",
      },
      {
        time: 573,
        text: "Combo again! That's two!",
      },
      {
        time: 578,
        text: "Keep it up! That's three!",
      },
      {
        time: 583,
        text: "Two more to go! That's four!",
      },
      {
        time: 588,
        text: "Last one, best one! That's five!",
      },
      {
        time: 593,
        text: 'Incredible! You just did a pro combo!',
      },
      {
        time: 598,
        text: 'FINAL CHALLENGE! Thirty seconds on the clock!',
      },
      {
        time: 603,
        text: 'Your choice! Any dribble we learned! Crossovers, between the legs, behind the back!',
      },
      {
        time: 610,
        text: 'As fast and fancy as you can be! Ready... set... GO!',
      },
      {
        time: 616,
        text: 'Go crazy! Show me your best moves!',
      },
      {
        time: 621,
        text: 'Ten seconds gone! Keep it up!',
      },
      {
        time: 631,
        text: 'Halfway there! You look like a superstar!',
      },
      {
        time: 639,
        text: 'Last ten seconds! Faster, faster, faster!',
      },
      {
        time: 649,
        text: 'Five! Four! Three! Two! One!',
      },
      {
        time: 655,
        text: 'And STOP!',
      },
      {
        time: 657,
        text: 'Wow! You did it!',
      },
      {
        time: 660,
        text: 'You are a dribbling champion! Awesome job today!',
      },
    ],
    timeTotal: 664,
    equipment: 'Basketball',
  },

  // done 1
  {
    id: 2004,
    name: 'Beginner Shooting',
    categories: ['Shooting', 'beginner'],
    sports: ['basketball'],
    difficulty: 'beginner',
    skill: 'Shooting',
    points: 15,
    instructions:
      "Get ready to become a basketball superstar! In this super fun game, we're going to learn how to shoot hoops like a pro! Let's power up our shots and score some awesome points!",
    timedInstructions: [
      {
        time: 0,
        text: "Hello basketball superstar! Let's get ready to play the ultimate shooting game! First, grab your basketball! If you don't have a hoop, that's okay! You can pretend and aim for a spot on the wall!",
      },
      {
        time: 15,
        text: "Let's start by getting our feet ready! Stand with your feet about as wide as your shoulders, like you're a strong superhero! Now, start bouncing on the balls of your feet! Light and springy!",
      },
      {
        time: 30,
        text: "Great! Keep bouncing! Now for our first challenge, we're going to become a shooting robot! We need perfect form!",
      },
      {
        time: 45,
        text: "First, let's learn to hold the ball! Hold it with one hand under the ball, like you're a waiter carrying a pizza! This is your shooting hand! Your other hand just rests on the side to help balance it. That's your guide hand!",
      },
      {
        time: 60,
        text: "Okay robot, let's get into shooting position! Bend your knees like you're about to spring up! Keep the ball right in front of you, ready to launch!",
      },
      {
        time: 75,
        text: "Now, without jumping, we're going to practice the arm motion! Push the ball up towards the sky, and when your arm is straight, flick your wrist forward like you're waving bye-bye to the ball! Let's try that 5 times! One!",
      },
      {
        time: 85,
        text: 'Two! Push and flick!',
      },
      {
        time: 95,
        text: 'Three! Great job!',
      },
      {
        time: 105,
        text: 'Four! Keep that elbow in!',
      },
      {
        time: 115,
        text: 'Five! Awesome robot form!',
      },
      {
        time: 125,
        text: "Okay, let's add the jump! Get in your strong stance, knees bent! This time when you push the ball up, jump straight up like a rocket! Ready? JUMP AND SHOOT! One!",
      },
      {
        time: 140,
        text: "Get your ball! Let's do it again! Knees bent, ball ready... JUMP AND SHOOT! Two!",
      },
      {
        time: 155,
        text: "Yes! That's it! Keep bouncing while you get your ball! Bounce, bounce, bounce!",
      },
      {
        time: 170,
        text: "Let's go again! JUMP AND SHOOT! Three!",
      },
      {
        time: 185,
        text: 'Two more! Make this your best one yet! Set your feet... bend your knees... JUMP AND SHOOT! Four!',
      },
      {
        time: 200,
        text: 'Last one for the robot challenge! Make it count! READY, SET, SHOOT! Five! Amazing job!',
      },
      {
        time: 215,
        text: "Shake out your arms! Shuffle your feet left and right! Keep moving! Don't stop!",
      },
      {
        time: 230,
        text: 'Okay! Challenge two is called Perfect Arc Power! We want our shots to go UP, UP, UP and then fall right into the basket, like a rainbow!',
      },
      {
        time: 245,
        text: "Stand super close to your basket. So close you can almost touch it! We're going to do 10 rainbow shots from here. No jumping needed for these!",
      },
      {
        time: 260,
        text: 'Just use your robot arm motion. Push the ball up high, and flick your wrist so it gently drops into the hoop. Ready? Shot one! Swish!',
      },
      {
        time: 275,
        text: 'Great! Shot two! All net!',
      },
      {
        time: 290,
        text: 'Shot three! See that rainbow arc?',
      },
      {
        time: 305,
        text: "Shot four! You're a machine!",
      },
      {
        time: 320,
        text: 'Shot five! Halfway there! Keep your eyes on the target!',
      },
      {
        time: 335,
        text: 'Shot six! Perfect form!',
      },
      {
        time: 350,
        text: "Shot seven! Don't forget to flick that wrist!",
      },
      {
        time: 365,
        text: 'Shot eight! Almost there!',
      },
      {
        time: 380,
        text: 'Shot nine! One more to go!',
      },
      {
        time: 395,
        text: 'Shot ten! You did it! High five!',
      },
      {
        time: 405,
        text: "Now, take one big step back! We're going to do 5 shots from here. This time, add your little jump! Remember, jump and shoot at the same time!",
      },
      {
        time: 420,
        text: "Let's go! Shot one! JUMP AND SHOOT!",
      },
      {
        time: 435,
        text: 'Shot two! Get that rainbow arc!',
      },
      {
        time: 450,
        text: 'Shot three! You got this!',
      },
      {
        time: 465,
        text: 'Shot four! Power up!',
      },
      {
        time: 480,
        text: "Shot five! Awesome! You've passed the Perfect Arc Power challenge!",
      },
      {
        time: 490,
        text: "Quick, shuffle your feet side to side! Let's get ready for the next level!",
      },
      {
        time: 500,
        text: "Welcome to the Spot Shooter Challenge! We're going to shoot from three different spots around the basket. Imagine there are three magic spots on the floor!",
      },
      {
        time: 515,
        text: 'Our first spot is right in front of the hoop, about three big steps away. Go stand there now! Bounce on your feet!',
      },
      {
        time: 530,
        text: "Your challenge is to make 3 shots from this spot! It's okay if you miss, just keep trying until you make three! Ready? Go!",
      },
      {
        time: 590,
        text: 'Keep going! Bend those knees! Eyes on the prize! You can do it!',
      },
      {
        time: 600,
        text: "Awesome! Once you've made your three shots, it's time to move to magic spot number two! This spot is on the right side of the hoop!",
      },
      {
        time: 615,
        text: "Same challenge here! Make 3 shots from the right side! Let's see that perfect form! GO!",
      },
      {
        time: 675,
        text: 'Focus! Remember the rainbow! Push the ball UP, not out! Keep trying!',
      },
      {
        time: 690,
        text: 'Great work! Now for the final spot! Move over to the left side of the hoop! You know what to do!',
      },
      {
        time: 705,
        text: "Three more makes from the left side! This is the last spot! Give it all you've got! Begin!",
      },
      {
        time: 765,
        text: "Don't give up! Every shot makes you stronger! You're almost there!",
      },
      {
        time: 780,
        text: 'Challenge complete! You are an official Spot Shooter! Quick, do 10 jumping jacks to celebrate! One, two, three, four, five, six, seven, eight, nine, ten!',
      },
      {
        time: 800,
        text: "Okay superstar, catch your breath! It's time for our FINAL challenge! The Lightning Round!",
      },
      {
        time: 815,
        text: 'For the next 90 seconds, you are going to shoot from anywhere you want in our magic spot area! The goal is to make as many baskets as you can before the timer runs out!',
      },
      {
        time: 830,
        text: "Every time you shoot, get your own rebound super fast and try again! Don't wait around! Go, go, go!",
      },
      {
        time: 845,
        text: 'The Lightning Round starts... NOW!',
      },
      {
        time: 900,
        text: 'Thirty seconds left! Speed up! How many more can you make? Faster, faster!',
      },
      {
        time: 920,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one! STOP!',
      },
      {
        time: 930,
        text: "Whew! Great job! How many did you make? Whatever the number is, that's your new high score to beat next time!",
      },
      {
        time: 945,
        text: 'You were amazing today! You learned how to be a shooting robot, how to make a perfect rainbow arc, and you beat the Spot Shooter and Lightning Round challenges! You are a true basketball pro!',
      },
      {
        time: 960,
        text: 'Workout complete! Awesome job, superstar!',
      },
    ],
    timeTotal: 960,
    equipment: 'Basketball and hoop (optional)',
  },

  // done 1
  {
    id: 2005,
    name: 'Super Shooter Challenge',
    categories: ['Shooting', 'intermediate'],
    sports: ['basketball'],
    difficulty: 'intermediate',
    skill: 'Shooting',
    points: 22,
    instructions:
      "Get ready to become a basketball superstar! In this adventure, we'll practice shooting like the pros, make tricky shots, and score tons of points! Let's get that ball in the hoop! Go, go, go!",
    timedInstructions: [
      {
        time: 0,
        text: "Let's go! Grab your basketball! First, stand right in front of the hoop, super close!",
      },
      {
        time: 10,
        text: "Hold the ball in your shooting hand, like you're a waiter holding a tray of pizza! Put your other hand on the side, just to help balance.",
      },
      {
        time: 20,
        text: 'Now, bend your knees! Get ready to spring up! BOING! And push the ball up towards the hoop!',
      },
      {
        time: 28,
        text: "Flick your wrist like you're reaching into a cookie jar on a high shelf! Let's try to make five shots!",
      },
      {
        time: 35,
        text: 'One! Good! Two! Awesome!',
      },
      {
        time: 42,
        text: 'Three! Keep that elbow in!',
      },
      {
        time: 49,
        text: "Four! Swish! Five! You're a natural!",
      },
      {
        time: 60,
        text: 'Great! Now, keep your feet moving! Bounce, bounce, bounce! Little hops! Stay light on your toes!',
      },
      {
        time: 75,
        text: "Let's do it again! Five more one-handed shots! Bend your knees, and SWISH!",
      },
      {
        time: 82,
        text: "One! Reach for that cookie jar! Two! That's it!",
      },
      {
        time: 90,
        text: "Three! You're on fire! Four! One more! Five!",
      },
      {
        time: 105,
        text: 'Take one step back! Just a little baby step! Still pretty close to the hoop. Bounce on your feet!',
      },
      {
        time: 120,
        text: "Okay, new challenge! Try to make the ball spin backwards! Flick that wrist extra hard when you shoot! Let's make five shots with super spin! Go!",
      },
      {
        time: 135,
        text: 'One! Two! Did you see it spin?',
      },
      {
        time: 150,
        text: 'Three! Awesome! Four! Last one!',
      },
      {
        time: 165,
        text: 'Five! Amazing job!',
      },
      {
        time: 180,
        text: "Time for a new game! We're shooting from three spots! First spot is on the right side of the hoop! Go there now!",
      },
      {
        time: 195,
        text: 'Get in your superhero stance! Feet ready, knees bent, eyes on the prize!',
      },
      {
        time: 210,
        text: 'You have one minute! Make as many shots as you can from this spot! Ready, set, GO!',
      },
      {
        time: 225,
        text: 'Get your rebound, run back to the spot, and shoot again! Go, go, go!',
      },
      {
        time: 240,
        text: 'Keep shooting! How many can you make? Faster, faster!',
      },
      {
        time: 255,
        text: "You've got this! Fifteen seconds left!",
      },
      {
        time: 270,
        text: "Time's up! Great job! Now, shuffle, shuffle, shuffle to the spot right in the middle!",
      },
      {
        time: 285,
        text: "Okay, one minute from the middle spot! Let's see if you can beat your score! Ready? GO!",
      },
      {
        time: 300,
        text: 'Bend those knees! Follow through! Great shot!',
      },
      {
        time: 315,
        text: 'Get the ball and hurry back! You are a super shooter!',
      },
      {
        time: 330,
        text: 'Halfway there! Keep up the speed!',
      },
      {
        time: 345,
        text: "Stop! Awesome hustle! Now, let's shuffle to the last spot on the left side! Keep bouncing!",
      },
      {
        time: 360,
        text: 'This is it! Last spot! One more minute! Try to get your best score yet! Ready, set, SHOOT!',
      },
      {
        time: 375,
        text: 'Push that ball high! Great form! Get your rebound!',
      },
      {
        time: 390,
        text: "Hustle back to the spot! Let's see another one go in!",
      },
      {
        time: 405,
        text: "Don't stop until you hear the buzzer! Ten seconds left!",
      },
      {
        time: 420,
        text: "BEEEEEP! Time is up! You did amazing! Grab a quick sip of water! You have one minute! But don't sit down, keep your feet moving! Jog in place!",
      },
      {
        time: 480,
        text: "Water break over! Let's add some action! Go back to the middle spot.",
      },
      {
        time: 495,
        text: "This time, you're going to take one big dribble, then jump into your shot! It's called a pound-shot! Pound the ball HARD, then hop into your superhero stance.",
      },
      {
        time: 510,
        text: "Let's try it! One minute! Pound, hop, shoot! Go!",
      },
      {
        time: 525,
        text: 'Pound it! Hop! Shoot! Get your rebound and do it again!',
      },
      {
        time: 540,
        text: 'Perfect! Keep that rhythm! Pound... hop... shoot!',
      },
      {
        time: 555,
        text: "You're a scoring machine! Fifteen seconds left!",
      },
      {
        time: 570,
        text: "Freeze! Great job! Now, let's try it from the right side spot! Shuffle over! Bounce on your feet!",
      },
      {
        time: 585,
        text: 'Same thing! One minute! Pound, hop, shoot! Ready, set, go!',
      },
      {
        time: 600,
        text: 'Dribble hard! Hop into your shot! Swish!',
      },
      {
        time: 615,
        text: "Let's see five more makes! Come on, you can do it!",
      },
      {
        time: 630,
        text: "Keep that energy up! Let's go!",
      },
      {
        time: 645,
        text: 'Stop! Okay, last spot! Shuffle over to the left! You know what to do!',
      },
      {
        time: 660,
        text: "One last minute on this drill! Give it everything you've got! Pound that ball like you're angry at the floor! Then a soft, high shot! Go, go, go!",
      },
      {
        time: 675,
        text: 'Faster! Pound, hop, shoot! Amazing!',
      },
      {
        time: 690,
        text: "Don't get tired now! Finish strong!",
      },
      {
        time: 705,
        text: 'Ten more seconds! How many more can you make?',
      },
      {
        time: 720,
        text: "Awesome work! Now for a secret weapon... the bank shot! We're going to aim for the little white square on the backboard.",
      },
      {
        time: 735,
        text: "Go to a spot on the right side, close to the hoop. See that top corner of the white square? That's your target!",
      },
      {
        time: 750,
        text: 'One minute to be the Bank Shot Boss from the right side! Kiss it off the glass! Go!',
      },
      {
        time: 765,
        text: "Aim for the square! It's like a trick shot! Bank it in!",
      },
      {
        time: 780,
        text: "Let's get five bank shots! Come on!",
      },
      {
        time: 795,
        text: 'Great shot! Do it again!',
      },
      {
        time: 810,
        text: 'Perfect! Now switch sides! Shuffle over to the left. Find that top corner of the square on the other side.',
      },
      {
        time: 825,
        text: "Another minute of bank shots! Let's go! Use the backboard! It's your best friend!",
      },
      {
        time: 840,
        text: "Bank it! That's one! Bank it! That's two!",
      },
      {
        time: 855,
        text: "Keep going! You're a master of angles!",
      },
      {
        time: 870,
        text: "Just a few more seconds! Let's get one more!",
      },
      {
        time: 885,
        text: 'Time! Great job! Bounce on your feet, we have one last super challenge!',
      },
      {
        time: 900,
        text: 'This is the Lightning Round! You have to make a shot from all three spots: right, middle, and left. As fast as you can!',
      },
      {
        time: 915,
        text: 'The clock is ticking for one minute! Make one from the right, then run to the middle, make one there, then run to the left and make one! If you finish, start over! Ready? GO!',
      },
      {
        time: 930,
        text: 'Hustle to the first spot! Make it!',
      },
      {
        time: 945,
        text: 'Now the middle! Quick, quick!',
      },
      {
        time: 960,
        text: 'Now the left! Did you make all three? Start over!',
      },
      {
        time: 975,
        text: "Keep going! Faster, faster! Don't just shoot, MAKE the shot! Fifteen seconds left!",
      },
      {
        time: 990,
        text: "Time's up! Woo! Okay, catch your breath! But keep shuffling! Now we're doing it with bank shots!",
      },
      {
        time: 1005,
        text: 'Make a bank shot from the right side, then a bank shot from the left side! As many rounds as you can in one minute!',
      },
      {
        time: 1020,
        text: 'This is the final challenge! Ready, set, LIGHTNING! Bank it from the right! Got it!',
      },
      {
        time: 1035,
        text: 'Now hustle to the left! Bank it! Yes!',
      },
      {
        time: 1050,
        text: "Do it again! Go, go, go! Don't stop moving!",
      },
      {
        time: 1065,
        text: 'You are a basketball machine! Ten, nine, eight, seven, six, five, four, three, two, one! DONE!',
      },
    ],
    timeTotal: 1080,
    equipment: 'Basketball and hoop',
  },

  // done 1
  {
    id: 2005,
    name: 'Super Shooter Challenge',
    categories: ['Shooting', 'intermediate'],
    sports: ['basketball'],
    difficulty: 'intermediate',
    skill: 'Shooting',
    points: 22,
    instructions:
      "Get ready to become a basketball superstar! In this adventure, we'll practice shooting like the pros, make tricky shots, and score tons of points! Let's get that ball in the hoop! Go, go, go!",
    timedInstructions: [
      {
        time: 0,
        text: "Let's go! Grab your basketball! First, stand right in front of the hoop, super close!",
      },
      {
        time: 10,
        text: "Hold the ball in your shooting hand, like you're a waiter holding a tray of pizza! Put your other hand on the side, just to help balance.",
      },
      {
        time: 20,
        text: 'Now, bend your knees! Get ready to spring up! BOING! And push the ball up towards the hoop!',
      },
      {
        time: 28,
        text: "Flick your wrist like you're reaching into a cookie jar on a high shelf! Let's try to make five shots!",
      },
      {
        time: 35,
        text: 'One! Good! Two! Awesome!',
      },
      {
        time: 42,
        text: 'Three! Keep that elbow in!',
      },
      {
        time: 49,
        text: "Four! Swish! Five! You're a natural!",
      },
      {
        time: 60,
        text: 'Great! Now, keep your feet moving! Bounce, bounce, bounce! Little hops! Stay light on your toes!',
      },
      {
        time: 75,
        text: "Let's do it again! Five more one-handed shots! Bend your knees, and SWISH!",
      },
      {
        time: 82,
        text: "One! Reach for that cookie jar! Two! That's it!",
      },
      {
        time: 90,
        text: "Three! You're on fire! Four! One more! Five!",
      },
      {
        time: 105,
        text: 'Take one step back! Just a little baby step! Still pretty close to the hoop. Bounce on your feet!',
      },
      {
        time: 120,
        text: "Okay, new challenge! Try to make the ball spin backwards! Flick that wrist extra hard when you shoot! Let's make five shots with super spin! Go!",
      },
      {
        time: 135,
        text: 'One! Two! Did you see it spin?',
      },
      {
        time: 150,
        text: 'Three! Awesome! Four! Last one!',
      },
      {
        time: 165,
        text: 'Five! Amazing job!',
      },
      {
        time: 180,
        text: "Time for a new game! We're shooting from three spots! First spot is on the right side of the hoop! Go there now!",
      },
      {
        time: 195,
        text: 'Get in your superhero stance! Feet ready, knees bent, eyes on the prize!',
      },
      {
        time: 210,
        text: 'You have one minute! Make as many shots as you can from this spot! Ready, set, GO!',
      },
      {
        time: 225,
        text: 'Get your rebound, run back to the spot, and shoot again! Go, go, go!',
      },
      {
        time: 240,
        text: 'Keep shooting! How many can you make? Faster, faster!',
      },
      {
        time: 255,
        text: "You've got this! Fifteen seconds left!",
      },
      {
        time: 270,
        text: "Time's up! Great job! Now, shuffle, shuffle, shuffle to the spot right in the middle!",
      },
      {
        time: 285,
        text: "Okay, one minute from the middle spot! Let's see if you can beat your score! Ready? GO!",
      },
      {
        time: 300,
        text: 'Bend those knees! Follow through! Great shot!',
      },
      {
        time: 315,
        text: 'Get the ball and hurry back! You are a super shooter!',
      },
      {
        time: 330,
        text: 'Halfway there! Keep up the speed!',
      },
      {
        time: 345,
        text: "Stop! Awesome hustle! Now, let's shuffle to the last spot on the left side! Keep bouncing!",
      },
      {
        time: 360,
        text: 'This is it! Last spot! One more minute! Try to get your best score yet! Ready, set, SHOOT!',
      },
      {
        time: 375,
        text: 'Push that ball high! Great form! Get your rebound!',
      },
      {
        time: 390,
        text: "Hustle back to the spot! Let's see another one go in!",
      },
      {
        time: 405,
        text: "Don't stop until you hear the buzzer! Ten seconds left!",
      },
      {
        time: 420,
        text: "BEEEEEP! Time is up! You did amazing! Grab a quick sip of water! You have one minute! But don't sit down, keep your feet moving! Jog in place!",
      },
      {
        time: 480,
        text: "Water break over! Let's add some action! Go back to the middle spot.",
      },
      {
        time: 495,
        text: "This time, you're going to take one big dribble, then jump into your shot! It's called a pound-shot! Pound the ball HARD, then hop into your superhero stance.",
      },
      {
        time: 510,
        text: "Let's try it! One minute! Pound, hop, shoot! Go!",
      },
      {
        time: 525,
        text: 'Pound it! Hop! Shoot! Get your rebound and do it again!',
      },
      {
        time: 540,
        text: 'Perfect! Keep that rhythm! Pound... hop... shoot!',
      },
      {
        time: 555,
        text: "You're a scoring machine! Fifteen seconds left!",
      },
      {
        time: 570,
        text: "Freeze! Great job! Now, let's try it from the right side spot! Shuffle over! Bounce on your feet!",
      },
      {
        time: 585,
        text: 'Same thing! One minute! Pound, hop, shoot! Ready, set, go!',
      },
      {
        time: 600,
        text: 'Dribble hard! Hop into your shot! Swish!',
      },
      {
        time: 615,
        text: "Let's see five more makes! Come on, you can do it!",
      },
      {
        time: 630,
        text: "Keep that energy up! Let's go!",
      },
      {
        time: 645,
        text: 'Stop! Okay, last spot! Shuffle over to the left! You know what to do!',
      },
      {
        time: 660,
        text: "One last minute on this drill! Give it everything you've got! Pound that ball like you're angry at the floor! Then a soft, high shot! Go, go, go!",
      },
      {
        time: 675,
        text: 'Faster! Pound, hop, shoot! Amazing!',
      },
      {
        time: 690,
        text: "Don't get tired now! Finish strong!",
      },
      {
        time: 705,
        text: 'Ten more seconds! How many more can you make?',
      },
      {
        time: 720,
        text: "Awesome work! Now for a secret weapon... the bank shot! We're going to aim for the little white square on the backboard.",
      },
      {
        time: 735,
        text: "Go to a spot on the right side, close to the hoop. See that top corner of the white square? That's your target!",
      },
      {
        time: 750,
        text: 'One minute to be the Bank Shot Boss from the right side! Kiss it off the glass! Go!',
      },
      {
        time: 765,
        text: "Aim for the square! It's like a trick shot! Bank it in!",
      },
      {
        time: 780,
        text: "Let's get five bank shots! Come on!",
      },
      {
        time: 795,
        text: 'Great shot! Do it again!',
      },
      {
        time: 810,
        text: 'Perfect! Now switch sides! Shuffle over to the left. Find that top corner of the square on the other side.',
      },
      {
        time: 825,
        text: "Another minute of bank shots! Let's go! Use the backboard! It's your best friend!",
      },
      {
        time: 840,
        text: "Bank it! That's one! Bank it! That's two!",
      },
      {
        time: 855,
        text: "Keep going! You're a master of angles!",
      },
      {
        time: 870,
        text: "Just a few more seconds! Let's get one more!",
      },
      {
        time: 885,
        text: 'Time! Great job! Bounce on your feet, we have one last super challenge!',
      },
      {
        time: 900,
        text: 'This is the Lightning Round! You have to make a shot from all three spots: right, middle, and left. As fast as you can!',
      },
      {
        time: 915,
        text: 'The clock is ticking for one minute! Make one from the right, then run to the middle, make one there, then run to the left and make one! If you finish, start over! Ready? GO!',
      },
      {
        time: 930,
        text: 'Hustle to the first spot! Make it!',
      },
      {
        time: 945,
        text: 'Now the middle! Quick, quick!',
      },
      {
        time: 960,
        text: 'Now the left! Did you make all three? Start over!',
      },
      {
        time: 975,
        text: "Keep going! Faster, faster! Don't just shoot, MAKE the shot! Fifteen seconds left!",
      },
      {
        time: 990,
        text: "Time's up! Woo! Okay, catch your breath! But keep shuffling! Now we're doing it with bank shots!",
      },
      {
        time: 1005,
        text: 'Make a bank shot from the right side, then a bank shot from the left side! As many rounds as you can in one minute!',
      },
      {
        time: 1020,
        text: 'This is the final challenge! Ready, set, LIGHTNING! Bank it from the right! Got it!',
      },
      {
        time: 1035,
        text: 'Now hustle to the left! Bank it! Yes!',
      },
      {
        time: 1050,
        text: "Do it again! Go, go, go! Don't stop moving!",
      },
      {
        time: 1065,
        text: 'You are a basketball machine! Ten, nine, eight, seven, six, five, four, three, two, one! DONE!',
      },
    ],
    timeTotal: 1080,
    equipment: 'Basketball and hoop',
  },

  // done 1
  {
    id: 2007,
    name: 'Basketball Defense Challenge!',
    categories: ['Defense', 'beginner'],
    sports: ['basketball'],
    difficulty: 'beginner',
    skill: 'Defense',
    points: 15,
    instructions:
      "Get ready to become a defense SUPERSTAR! This is a super fun, non-stop game where you'll learn to move like a pro basketball player. We'll learn the Power Stance, do speedy Side Shuffles, and even practice blocking shots! No boring stuff, just pure action and fun to make you the best defender on the court. Let's go!",
    timeTotal: 945,
    timedInstructions: [
      {
        time: 0,
        text: "Hello! Get ready for the super fun Basketball Defense Challenge! We're going to learn how to be the best defender ever! No ball needed, just bring your energy! Let's go!",
      },
      {
        time: 10,
        text: "First, let's learn the Power Stance! Get your feet wide apart, wider than your shoulders! Now, bend your knees and sink your bottom down like you're sitting in a secret, invisible chair! Awesome!",
      },
      {
        time: 20,
        text: "Now for your arms! Put them out wide to the sides, like you're a giant airplane ready for takeoff! Keep your back straight and your eyes looking straight ahead! This is your Power Stance! You look tough!",
      },
      {
        time: 30,
        text: "Hold that stance! Don't move! You're a statue! A super strong basketball statue! Five, four, three, two, one! Great job! Shake it out!",
      },
      {
        time: 40,
        text: "Okay, back to your Power Stance! Feet wide, get low, arms out! Now we're going to do the Side Shuffle! We're going to slide to the right without crossing our feet. Ready? Go! Step with your right foot, then slide your left foot over. Step, slide, step, slide! Like a crab!",
      },
      {
        time: 55,
        text: "Keep shuffling right! Stay low in your chair! Don't stand up! Keep those airplane arms out! Five more seconds! Four, three, two, one! Stop! Back in the middle!",
      },
      {
        time: 65,
        text: "Keep bouncing on your feet! Light, light, light! Ready to go left? Back in your Power Stance! Go! Shuffle to the left! Step with your left foot, slide your right foot. Step, slide, step, slide! You've got this!",
      },
      {
        time: 80,
        text: "Faster! A little faster! Imagine you're guarding a super speedy player! Stay low! Five, four, three, two, one! Stop! Good job! Shake your legs out!",
      },
      {
        time: 90,
        text: "Let's do that one more time! It's Round Two! Get in your Power Stance! Now! Shuffle right! Go! Quick feet, quick feet! Don't let anyone get past you!",
      },
      {
        time: 105,
        text: 'And stop! Now shuffle left! Go! Go! Go! Be a wall! A super fast wall! Stay low!',
      },
      {
        time: 120,
        text: 'And stop! Awesome work! Take a little breather! Just march in place. March, march, march. Check your airplane arms, are they still out?',
      },
      {
        time: 135,
        text: "Okay, new move! We're going to add Annoying Hands! Get back in your Power Stance. Now, while you stay low, wave your hands all around in front of you. Wiggle your fingers! Like you have spaghetti for arms! This bothers the person with the ball!",
      },
      {
        time: 150,
        text: "Now, let's add the Annoying Hands to our shuffle! Power Stance! Annoying Hands up! Shuffle to the right! Go! Wiggle those fingers while you slide! Step, slide, wiggle, wiggle!",
      },
      {
        time: 165,
        text: "Keep going! You're a super annoying defender! Nobody can see past your hands! Five, four, three, two, one! Stop! Back to the middle!",
      },
      {
        time: 175,
        text: 'Now to the left! Same thing! Power Stance, Annoying Hands, and shuffle left! Go! Be as wiggly and distracting as you can! Stay low!',
      },
      {
        time: 190,
        text: 'Five more seconds! Faster shuffles! Faster hands! Three, two, one! And stop! Whew! Great job! High five the air!',
      },
      {
        time: 200,
        text: "Okay, let's take a quick 10-second break. Grab a super quick sip of water if you need it! Be fast like a ninja!",
      },
      {
        time: 210,
        text: "Time for a game! It's called Defender Red Light, Green Light! When I say Green Light, you shuffle left or right with your Annoying Hands. When I say Red Light, you freeze in your Power Stance! Got it?",
      },
      {
        time: 225,
        text: 'Ready? Get in your Power Stance! Green Light! Shuffle any way you want! Left or right! Go, go, go!',
      },
      {
        time: 235,
        text: "Red Light! Freeze! Don't even blink! Are you low? Are your arms out? Perfect!",
      },
      {
        time: 245,
        text: 'Green Light! Go again! Shuffle, shuffle, shuffle! Quick feet! Annoying Hands!',
      },
      {
        time: 255,
        text: 'Red Light! Freeze! Like an ice sculpture! Hold it!',
      },
      {
        time: 265,
        text: 'Green Light! Go! Be a shuffling machine! Faster this time!',
      },
      {
        time: 275,
        text: 'Red Light! Freeze! Last one! Hold it super still! Five, four, three, two, one! Awesome! Shake it out!',
      },
      {
        time: 290,
        text: "That was fun! Let's play one more round of Defender Red Light, Green Light! A little faster this time! Ready? Power Stance!",
      },
      {
        time: 300,
        text: 'Green Light! Go! Shuffle, shuffle! Side to side!',
      },
      {
        time: 308,
        text: 'Red Light! Freeze!',
      },
      {
        time: 315,
        text: 'Green Light! Move those feet!',
      },
      {
        time: 323,
        text: 'Red Light! Freeze!',
      },
      {
        time: 330,
        text: 'Green Light! Super speed! Go, go, go, go, go!',
      },
      {
        time: 340,
        text: 'Red Light! Freeze! Best freeze yet! Hold it! And relax! You are a Red Light, Green Light champion!',
      },
      {
        time: 355,
        text: "Okay, next challenge! This is the Run and Stop! Imagine the player you're guarding is far away. We have to run to them and get in our Power Stance.",
      },
      {
        time: 365,
        text: 'Start at one end of your space. When I say go, take five super fast, choppy little steps forward, then land in a perfect Power Stance! Ready? Go! Run, run, run, run, run, and STOP! Power Stance!',
      },
      {
        time: 375,
        text: 'Hold that stance! Is it a good one? Feet wide, sitting low, arms out? Yes! Okay, jog back to where you started.',
      },
      {
        time: 385,
        text: "Let's do it again! Ready? Go! Five choppy steps and STOP! Freeze in that Power Stance!",
      },
      {
        time: 395,
        text: 'Good! Back to the start! Keep bouncing while you wait!',
      },
      {
        time: 405,
        text: "Again! Go! Run and STOP! Perfect stance! You're getting so good at this!",
      },
      {
        time: 415,
        text: 'Jog back! We have two more! Keep up the great energy!',
      },
      {
        time: 425,
        text: 'Ready? Go! Run and STOP! Freeze!',
      },
      {
        time: 435,
        text: 'Last one! Make it your best one! Go! Fast feet, and STICK that landing! Power Stance! Hold it! Three, two, one! Amazing!',
      },
      {
        time: 450,
        text: "Now, let's add a jump! When a player shoots, you have to try and block it! So we will do our Run and Stop, but at the end, we jump straight up with one hand high in the air!",
      },
      {
        time: 465,
        text: "Let's try it! Start back again. When I say 'Block the Shot', you do your five choppy steps, stop, and then JUMP straight up! Like you're trying to touch the ceiling! Ready?",
      },
      {
        time: 480,
        text: 'Block the Shot! Run, run, run, stop, JUMP! Land softly on your feet. Great!',
      },
      {
        time: 490,
        text: "Jog back to the start! Bounce on your feet! Don't stop moving!",
      },
      {
        time: 500,
        text: 'Again! Block the Shot! Go! Fast steps and a huge JUMP! Reach for the sky!',
      },
      {
        time: 510,
        text: "Good! Jog back! Let's do three more!",
      },
      {
        time: 520,
        text: 'Ready? Block the Shot! Run and JUMP!',
      },
      {
        time: 530,
        text: 'Awesome! Two more to go! You can do it!',
      },
      {
        time: 540,
        text: 'Block the Shot! Get up there! How high can you jump?',
      },
      {
        time: 550,
        text: 'Last one! The highest jump of the day! Ready? Block the Shot! Go! Go! Go! JUMP!',
      },
      {
        time: 565,
        text: "Excellent work! March in place and catch your breath. You're doing great!",
      },
      {
        time: 580,
        text: "Now for the ultimate combo challenge! We're going to put it all together. We will shuffle, then Run and Stop, then shuffle again! Listen for my commands!",
      },
      {
        time: 595,
        text: 'Get in your Power Stance in the middle of your space! Ready? Shuffle right! Go!',
      },
      {
        time: 605,
        text: 'Now, Run and Stop! Run forward and freeze in your stance!',
      },
      {
        time: 615,
        text: 'Good! Now shuffle left! Go!',
      },
      {
        time: 625,
        text: "And stop! Great combo! Let's try that again. Jog back to the middle.",
      },
      {
        time: 635,
        text: 'Round two! Power Stance! Shuffle left! Go!',
      },
      {
        time: 645,
        text: 'Run and Stop! Fast feet, then freeze!',
      },
      {
        time: 655,
        text: 'Shuffle right! Go! Go! Go!',
      },
      {
        time: 665,
        text: "And stop! You're a defensive superstar! Let's do one more round, but this time we will add the Block the Shot jump!",
      },
      {
        time: 680,
        text: 'Back to the middle! Get ready! Power Stance!',
      },
      {
        time: 688,
        text: 'Shuffle right! Go!',
      },
      {
        time: 696,
        text: 'Now, Block the Shot! Run forward and JUMP!',
      },
      {
        time: 706,
        text: 'Land soft! Now shuffle left! Go!',
      },
      {
        time: 716,
        text: 'And STOP! You did it! That was the full defensive move! High five yourself!',
      },
      {
        time: 730,
        text: "Okay, we're almost done! Let's finish with some super speed drills to make our feet even faster! This is the Super Speed Shuffle!",
      },
      {
        time: 740,
        text: 'We will shuffle back and forth between two spots as fast as we can for 20 seconds! Then we rest for 10 seconds. We will do this three times! Ready for the challenge?',
      },
      {
        time: 755,
        text: "Get in your Power Stance! The first 20-second round starts... NOW! Go! Shuffle right, shuffle left! As fast as you can! Don't cross your feet! Stay low!",
      },
      {
        time: 765,
        text: 'Ten more seconds! Faster, faster, faster! Come on, you got this!',
      },
      {
        time: 775,
        text: 'Three, two, one, and REST! Just for 10 seconds. Shake your legs out. Breathe.',
      },
      {
        time: 785,
        text: "Round two! Get ready! Set! Go! Super Speed Shuffle! Can you go faster than last time? I think you can! Let's see it!",
      },
      {
        time: 795,
        text: 'Halfway there! Keep pushing! Quick feet, quick feet!',
      },
      {
        time: 805,
        text: 'Three, two, one, REST! Ten seconds. Deep breath in, deep breath out. One more round left!',
      },
      {
        time: 815,
        text: 'This is it! The final round! Make it your fastest one ever! Ready? Set? GO! Go all out! This is your super power boost! Empty the tank!',
      },
      {
        time: 825,
        text: "Ten more seconds! Don't stop! Don't slow down! You are a blur!",
      },
      {
        time: 835,
        text: 'Five, four, three, two, one! AND DONE! You did it! Awesome job!',
      },
      {
        time: 850,
        text: 'Wow! Take a minute to march in place and slow your body down. That was hard work!',
      },
      {
        time: 865,
        text: "For our final, final challenge, let's play one quick game of Defender Freeze Dance. I'll call out moves, and when I yell FREEZE, you stop in that position!",
      },
      {
        time: 880,
        text: 'Ready? Side Shuffle! Go! Shuffle left and right! Have fun with it!',
      },
      {
        time: 890,
        text: 'Now do the Block the Shot! Run forward and JUMP!',
      },
      {
        time: 898,
        text: 'Back to shuffling! Go, go, go!',
      },
      {
        time: 905,
        text: 'Annoying Hands! Wiggle them everywhere!',
      },
      {
        time: 912,
        text: 'Block the Shot again! JUMP!',
      },
      {
        time: 920,
        text: "Side shuffle and... FREEZE! Hold that Power Stance! Don't move a muscle!",
      },
      {
        time: 930,
        text: 'And relax! You are an official Basketball Defense Superstar! You learned the Power Stance, the Side Shuffle, Annoying Hands, and how to Block the Shot! You were amazing!',
      },
      {
        time: 940,
        text: 'Great job today! Give yourself a big clap! See you next time for another challenge!',
      },
    ],
    equipment: 'None',
  },

  // done 1
  {
    id: 2008,
    name: 'Intermediate Defense',
    categories: ['Defense', 'intermediate'],
    sports: ['basketball'],
    difficulty: 'intermediate',
    skill: 'Defense',
    points: 20,
    instructions:
      "Get ready for a super fun basketball mission! We are going to become DEFENSE SUPERSTARS! You will learn to move like a ninja, stop anyone with the ball, and have lightning-fast hands. Let's go!",
    timedInstructions: [
      {
        time: 0,
        text: "Hello! Get ready for a super fun basketball defense mission! Let's become defense superstars! Ready?",
      },
      {
        time: 7,
        text: "First, let's learn the Superhero Stance! Get your feet super wide, wider than your shoulders! Awesome!",
      },
      {
        time: 15,
        text: "Now, bend your knees like you're about to sit in a chair, but don't sit down! Stay low!",
      },
      {
        time: 22,
        text: 'Great! Now put both your hands out to the sides, like you have superhero shields. This is your guard!',
      },
      {
        time: 30,
        text: "Hold that Superhero Stance! Don't stand up! Keep your back straight! You look like a real defender!",
      },
      {
        time: 40,
        text: 'Start bouncing on the balls of your feet! A little bounce, bounce, bounce! Stay ready!',
      },
      {
        time: 50,
        text: 'Keep bouncing! Now look to your left! Now look to your right! Keep your head up and looking around!',
      },
      {
        time: 60,
        text: 'Perfect! Shake it out for a second! Wiggle your arms and legs! Three, two, one, and FREEZE!',
      },
      {
        time: 70,
        text: "Back into your Superhero Stance! Feet wide, knees bent, hands out! Let's learn to move!",
      },
      {
        time: 80,
        text: "We're going to do the Crab Walk! Stay low and slide your feet. Never cross your feet! Ready?",
      },
      {
        time: 90,
        text: 'Take two big slides to your right! Slide, step! Slide, step! Good! Now two big slides to your left! Slide, step! Slide, step!',
      },
      {
        time: 103,
        text: "Let's make it a game! Right, right! Left, left! Stay low the whole time! Don't pop up!",
      },
      {
        time: 115,
        text: 'Keep shuffling! Bounce on your feet while you do it! This keeps you fast! Right, right! Left, left!',
      },
      {
        time: 130,
        text: 'Faster now! A super fast Crab Walk! Right, right, left, left! Go, go, go!',
      },
      {
        time: 145,
        text: "And STOP! Hold your Superhero Stance! Don't move! You're a statue!",
      },
      {
        time: 155,
        text: "Great job! Let's do that again! Ready? Five, four, three, two, one, GO! Shuffle right, shuffle left! Faster!",
      },
      {
        time: 170,
        text: "Imagine the floor is hot lava and you have to slide over it! Don't let your feet get burned! Keep shuffling!",
      },
      {
        time: 185,
        text: 'Ten more seconds of shuffling! Go as fast as you can! Ten, nine, eight, seven, six, five, four, three, two, one! FREEZE!',
      },
      {
        time: 200,
        text: 'Awesome! Take a quick breath! Wiggle it out! Next up is the Run and Stop!',
      },
      {
        time: 210,
        text: 'Imagine a player just got the ball far away from you! You have to run to them super fast!',
      },
      {
        time: 218,
        text: "Here's how we do it! We take three super fast running steps... one, two, three! Then, STOP!",
      },
      {
        time: 226,
        text: 'To stop, you chop your feet really, really fast and land in your Superhero Stance with your hands up!',
      },
      {
        time: 236,
        text: "Let's try it! Start back here... Ready? GO! Run, run, run, and CHOP-CHOP-CHOP your feet to a stop! Hands up!",
      },
      {
        time: 248,
        text: "Let's go back and do it again! It's like a game of Red Light, Green Light! Ready... GREEN LIGHT! Run and... RED LIGHT! Stop!",
      },
      {
        time: 261,
        text: "Awesome stop! Make sure you land low in your stance. Let's try it again! Go back... Ready... GO! Run, run, run, STOP!",
      },
      {
        time: 275,
        text: "This time, when you stop, yell 'BALL!' as loud as you can! Ready? GO! Run, run, run, BALL!",
      },
      {
        time: 287,
        text: "That was great! Reset! Keep bouncing on your feet! Let's do two more! GO! Run and stop! Hands up!",
      },
      {
        time: 300,
        text: 'Last one! Make it your best one! As fast as you can! GO! Run, run, run, CHOP, CHOP, CHOP, FREEZE!',
      },
      {
        time: 315,
        text: "Fantastic! Okay, time for a speed challenge! It's called Shuffle Mania! Get in your Superhero Stance!",
      },
      {
        time: 325,
        text: "For one whole minute, we are going to shuffle! I'll tell you which way to go! Don't stop moving! Ready? GO!",
      },
      {
        time: 335,
        text: 'Shuffle right! Keep going right! Now shuffle left! Keep going left! Stay low!',
      },
      {
        time: 345,
        text: "Two right, two left! Go! One, two, one, two! Faster! Imagine you're guarding a super fast player!",
      },
      {
        time: 360,
        text: 'Shuffle right! Stop! Shuffle left! Stop! Shuffle right! Stop! Shuffle left! Stop! Good!',
      },
      {
        time: 375,
        text: "You're halfway there! Keep breathing! Don't stand up! Stay low, stay low!",
      },
      {
        time: 385,
        text: 'Last twenty seconds! Shuffle anywhere you want, but keep shuffling! Go crazy! Left, right, back, forward! Go, go, go!',
      },
      {
        time: 400,
        text: 'Five, four, three, two, one... and FREEZE! Awesome work!',
      },
      {
        time: 410,
        text: 'Okay, new skill! Time for Quick Hands! Get back in your Superhero Stance!',
      },
      {
        time: 420,
        text: 'Imagine the player in front of you is dribbling the basketball. Your hands have to be lightning fast to poke it away!',
      },
      {
        time: 430,
        text: "Stay low! We're going to practice swiping at the ball. With your right hand, swipe down and forward like you're poking a button. POKE!",
      },
      {
        time: 442,
        text: 'Now your left hand! POKE! Keep your body still, just your hand moves. Right hand! POKE! Left hand! POKE!',
      },
      {
        time: 455,
        text: "Let's do a quick hands drill! We're going to do ten pokes as fast as we can, switching hands each time! Ready? GO!",
      },
      {
        time: 465,
        text: 'One, two, three, four, five, six, seven, eight, nine, ten! Wow, that was fast!',
      },
      {
        time: 475,
        text: 'Keep bouncing on your feet! Check your stance! Are you still low? Are your hands ready?',
      },
      {
        time: 485,
        text: "Let's do another Quick Hands drill! This time, I'll yell 'RIGHT' or 'LEFT'! You poke with that hand! Ready?",
      },
      {
        time: 495,
        text: 'RIGHT! ... LEFT! ... LEFT! ... RIGHT! ... RIGHT! ... LEFT! Keep your eyes up!',
      },
      {
        time: 510,
        text: 'Great reflexes! Now, ten more pokes as fast as you can! Ready, set, GO! One, two, three, four, five, six, seven, eight, nine, ten!',
      },
      {
        time: 525,
        text: 'Awesome job! Shake your hands out! Now for a new defense weapon! The Super Block!',
      },
      {
        time: 535,
        text: 'This is for when the other player tries to shoot! Get in your Superhero Stance. When a player shoots, you jump straight up, not forward!',
      },
      {
        time: 547,
        text: "Jump straight up, and reach one hand high into the sky like you're trying to touch the ceiling! Then land softly back on your feet.",
      },
      {
        time: 558,
        text: "Let's practice! I'll yell SHOOTER! and you do a Super Block! Ready? SHOOTER! Jump! Good landing!",
      },
      {
        time: 570,
        text: 'Get back in your stance right after you land! Be ready! Ready? SHOOTER! Jump high!',
      },
      {
        time: 582,
        text: "Remember to land softly, bend your knees! Don't land with stiff legs. Let's do three in a row! Ready?",
      },
      {
        time: 592,
        text: 'SHOOTER! jump! ... SHOOTER! jump! ... SHOOTER! jump! ... Great job!',
      },
      {
        time: 605,
        text: "Let's combine it with a Run and Stop. Run forward, stop, and then JUMP for the Super Block! Ready? GO!",
      },
      {
        time: 617,
        text: "Run, chop your feet, STOP... and JUMP! Perfect! Let's do that two more times!",
      },
      {
        time: 630,
        text: 'Go back... RUN AND BLOCK! Good! One more time! RUN AND BLOCK! Amazing!',
      },
      {
        time: 645,
        text: "You've got the Super Block! Shake it out! Now for the ultimate challenge... The Mirror Game!",
      },
      {
        time: 655,
        text: 'Get in your Superhero Stance. I am the player with the ball! You have to be my mirror. Everywhere I go, you go!',
      },
      {
        time: 665,
        text: 'Stay in front of me! Ready? The player is shuffling to your right! Follow me! Shuffle, shuffle!',
      },
      {
        time: 675,
        text: "Now they're going left! Go left! Stay low! Don't let them get past you!",
      },
      {
        time: 685,
        text: "The player is stepping back! You take one step closer! Good! They're coming forward! You take one step back!",
      },
      {
        time: 698,
        text: "Keep mirroring me! Right! Left! Right, right! Left! Stay with me! You're a defensive wall!",
      },
      {
        time: 712,
        text: 'The player is standing still, dribbling... Get your hands ready... wait for it... wait for it...',
      },
      {
        time: 722,
        text: "Back to moving! Left! Right! Keep your feet moving! Don't get lazy! Stay low!",
      },
      {
        time: 735,
        text: 'Fifteen more seconds of the Mirror Game! Go, go, go! Stay in front of me! Left, right, back, forward, right, left! You got this!',
      },
      {
        time: 750,
        text: "And FREEZE! Whew! That's hard work! You are a mirroring master!",
      },
      {
        time: 760,
        text: "Okay, let's add Quick Hands to the Mirror Game. This is the real deal!",
      },
      {
        time: 770,
        text: "Get in your stance! Start mirroring me. I'm going right... I'm going left...",
      },
      {
        time: 780,
        text: 'The player slows down... NOW! QUICK HANDS! Poke, poke, poke! Try to get the ball!',
      },
      {
        time: 790,
        text: "And back to mirroring! They're moving again! Left, left, right! Stay with them!",
      },
      {
        time: 800,
        text: 'They slowed down again! Get ready... QUICK HANDS! Go, go, go! Poke with your right, poke with your left!',
      },
      {
        time: 812,
        text: "Awesome! Back to mirroring! They're trying to trick you! Right! Fake left, go right again! Stay there!",
      },
      {
        time: 825,
        text: "Last chance to steal! They're tired... QUICK HANDS! As fast as you can! Five, four, three, two, one!",
      },
      {
        time: 838,
        text: "And STOP! Incredible! Now, let's combine the Mirror Game with your Super Block!",
      },
      {
        time: 848,
        text: "Start mirroring me! I'm the player with the ball! Going right... going left... stay with me...",
      },
      {
        time: 860,
        text: "Uh oh, I'm stopping to shoot! SUPER BLOCK! Jump straight up! Reach for it!",
      },
      {
        time: 870,
        text: "Good! Land and get right back to mirroring! I got the ball back! I'm going left... now right...",
      },
      {
        time: 883,
        text: "I'm shooting again! SUPER BLOCK! Higher this time! Try to touch the sky!",
      },
      {
        time: 893,
        text: "Land softly! Back in your stance! Keep mirroring me! Don't let me get away! Right, right, left!",
      },
      {
        time: 906,
        text: "Last one! I'm faking... I'm faking... NOW I'M SHOOTING! SUPER BLOCK! Go, go, go!",
      },
      {
        time: 918,
        text: 'And FREEZE! You are a shot blocking machine! Take a ten second power break. Big breath in... and out.',
      },
      {
        time: 930,
        text: "Break's over! It is time! The final, final challenge! The Defense Gauntlet! We put EVERYTHING together!",
      },
      {
        time: 942,
        text: 'Listen to my commands and do them as fast as you can! Get in your Superhero Stance! Bounce on your feet!',
      },
      {
        time: 952,
        text: 'COMMAND ONE! Two shuffles right, two shuffles left! GO!',
      },
      {
        time: 962,
        text: 'COMMAND TWO! Run and Stop, forward! GO! ... And jog back!',
      },
      {
        time: 972,
        text: 'COMMAND THREE! Ten Quick Hands! GO! One, two, three, four, five, six, seven, eight, nine, ten!',
      },
      {
        time: 984,
        text: 'COMMAND FOUR! A Run and Stop with a Super Block at the end! GO! Run, stop, JUMP!',
      },
      {
        time: 996,
        text: 'COMMAND FIVE! Mirror Game! The player is going right, left, right! Stay with them!',
      },
      {
        time: 1008,
        text: 'AND FREEZE! That was Round One of the Gauntlet! Get ready for Round Two, the non-stop round!',
      },
      {
        time: 1018,
        text: 'DEEP BREATH! GAUNTLET GO! Two shuffles right, two left! Run and Stop! Ten Quick Hands! Run and Block! Mirror me! Right! Left! Right again! And STOP!',
      },
      {
        time: 1038,
        text: "You did it! But can you do it... SUPER DUPER FAST?! This is the last challenge of the day! Give it all you've got!",
      },
      {
        time: 1050,
        text: 'FINAL GAUNTLET... GO! Shuffle! Run and Stop! Quick Hands! Run and Block! Mirror me, faster! Right, left, right, left! AND FREEZE!',
      },
      {
        time: 1065,
        text: 'YES! YOU DID IT! MISSION COMPLETE! You are an official Defense Superstar! Amazing work today!',
      },
    ],
    timeTotal: 1065,
    equipment: 'None',
  },

  // done 1
  {
    id: 2009,
    name: 'Advanced Defense',
    categories: ['Defense', 'advanced'],
    sports: ['basketball'],
    difficulty: 'advanced',
    skill: 'Defense',
    points: 18,
    instructions:
      "Get ready to become a defensive superstar! This is Advanced Defense, where we learn secret moves to stop anyone on the basketball court! We'll practice shuffling like a crab, running with choppy feet, and even playing defensive freeze dance! It's a non-stop challenge to turn you into a lockdown defender. Let's go!",
    timedInstructions: [
      {
        time: 0,
        text: "Hello future basketball star! Are you ready for a super fun challenge? Let's go!",
      },
      {
        time: 7,
        text: "We're going to become defensive giants! Get on your tippy-toes and start bouncing! Bounce, bounce, bounce!",
      },
      {
        time: 15,
        text: "Awesome! Now, let's learn the Superhero Stance! Spread your feet out wide, wider than your shoulders!",
      },
      {
        time: 22,
        text: "Now, bend your knees like you're about to sit in a secret, invisible chair! Get low!",
      },
      {
        time: 28,
        text: 'Great! Put your hands up and out, like you have giant shields protecting you! Hold that power stance!',
      },
      {
        time: 38,
        text: "Okay Superhero! Stay low! We're going to shuffle sideways, like a sneaky crab! Don't click your heels or cross your feet!",
      },
      {
        time: 48,
        text: "Let's go to the right! Big step with your right foot, then your left foot follows! Keep your feet wide! Let's go! Shuffle, shuffle, shuffle!",
      },
      {
        time: 60,
        text: 'Keep going right! Stay low in your superhero stance! Your legs are like strong springs! Bounce, bounce, shuffle!',
      },
      {
        time: 75,
        text: "Don't stand up! The floor is lava and you have to stay low! Keep shuffling right!",
      },
      {
        time: 90,
        text: "Fifteen more seconds this way! Keep those shield hands up! Let's go!",
      },
      {
        time: 105,
        text: "Perfect! Now let's go the other way! To the left! Big step with your left foot, and the right foot follows! Go!",
      },
      {
        time: 120,
        text: 'Shuffle, shuffle, shuffle to the left! Are you staying low? Check your stance! Knees bent!',
      },
      {
        time: 135,
        text: "Faster now! Quick feet! Imagine you're guarding the fastest player in the world! Go, go, go!",
      },
      {
        time: 150,
        text: "Keep it up! Don't let them get by! Fifteen more seconds of super shuffles to the left!",
      },
      {
        time: 165,
        text: "SPEED DRILL! Let's see how fast you can go! Three shuffles right, GO! One, two, three!",
      },
      {
        time: 172,
        text: 'Now three shuffles left, GO! One, two, three!',
      },
      {
        time: 178,
        text: 'Faster! Right, right, right! And left, left, left! Come on, quick feet!',
      },
      {
        time: 185,
        text: 'Again! Right, right, right! And left, left, left! Five, four, three, two, one! STOP!',
      },
      {
        time: 195,
        text: 'Whew! Great shuffling! Shake your legs out for a second! Now get right back into your Superhero Stance! Quick, quick, quick!',
      },
      {
        time: 205,
        text: 'Here comes the fun part! Imagine a super-fast player is right in front of you! You have to stay with them!',
      },
      {
        time: 215,
        text: "I'll call out directions, you shuffle to stay in front of them! Ready? Shuffle RIGHT!",
      },
      {
        time: 222,
        text: 'Now shuffle LEFT! Good! Stay low!',
      },
      {
        time: 228,
        text: "Two big slides FORWARD! Get closer! Don't let them shoot!",
      },
      {
        time: 235,
        text: 'Now two slides BACKWARDS! Give them some space!',
      },
      {
        time: 242,
        text: "Keep moving! Don't get caught standing still! Bounce on your feet!",
      },
      {
        time: 250,
        text: "They're going LEFT! Go left, go left!",
      },
      {
        time: 257,
        text: 'Now RIGHT! Quick, quick, quick!',
      },
      {
        time: 264,
        text: "BACKWARDS! Don't let them drive past you!",
      },
      {
        time: 270,
        text: 'FORWARD! Put the pressure on!',
      },
      {
        time: 276,
        text: "You've got them trapped! Keep shuffling left and right, just a little bit! Keep your feet buzzing!",
      },
      {
        time: 290,
        text: 'Great work! Keep it up! Over a minute to go on this drill! You can do it!',
      },
      {
        time: 300,
        text: "Let's go! RIGHT! LEFT! FORWARD! BACK!",
      },
      {
        time: 315,
        text: "Stay in front of your imaginary player! Don't look at your feet, look at their tummy! That's the secret!",
      },
      {
        time: 330,
        text: 'Fifteen seconds left! Go as fast as you can! Any direction you want! Go, go, go!',
      },
      {
        time: 345,
        text: 'QUICK FEET CHALLENGE! Stay in your stance and patter your feet on the floor like a super-fast drumroll! Go!',
      },
      {
        time: 355,
        text: 'Faster, faster, faster! Ten, nine, eight, seven, six, five, four, three, two, one! And STOP! Amazing!',
      },
      {
        time: 370,
        text: "Okay, time for a 30-second power-up! Stand up tall and do some quick jumping jacks! Go! Let's get that energy way up!",
      },
      {
        time: 385,
        text: "Keep moving, keep smiling! You're doing great! Ten seconds left!",
      },
      {
        time: 400,
        text: 'Alright, defender! Back to work! Time for Closeout Chaos! Get ready!',
      },
      {
        time: 408,
        text: 'Imagine a player far away is about to shoot! We have to run to them! This is a CLOSEOUT!',
      },
      {
        time: 415,
        text: 'When we get close, we take tiny, choppy, stompy feet! Stomp, stomp, stomp, stomp! And get a hand up high to block the shot!',
      },
      {
        time: 425,
        text: "Let's practice! From here, run forward two steps then CHOPPY FEET and hand up! Ready? GO! Run and CHOP!",
      },
      {
        time: 432,
        text: "Great! Back to the start. Let's do it again! Run and CHOP! Get that hand way up!",
      },
      {
        time: 440,
        text: 'Now for Closeout Chaos! There are shooters all around you! In front, behind, left, and right!',
      },
      {
        time: 448,
        text: 'When I shout a direction, you run a few steps and do your choppy feet closeout! Ready? FRONT! Run and CHOP!',
      },
      {
        time: 456,
        text: 'Awesome! Get back to the middle, stay bouncing! Ready for the next one?',
      },
      {
        time: 462,
        text: 'LEFT! Run and CHOP! Get that hand up!',
      },
      {
        time: 470,
        text: 'Back to the middle! Keep those feet ready!',
      },
      {
        time: 476,
        text: 'BEHIND YOU! Turn, run, and CHOP!',
      },
      {
        time: 484,
        text: "Back to the middle! You're a shot-blocking machine!",
      },
      {
        time: 490,
        text: 'RIGHT! Run and CHOP! Back to the middle!',
      },
      {
        time: 500,
        text: "Let's go faster! FRONT! ... Back to the middle! LEFT! ... Back to the middle!",
      },
      {
        time: 515,
        text: 'RIGHT! ... Back to the middle! BEHIND! ... Back to the middle!',
      },
      {
        time: 530,
        text: "Keep your feet fast and your hands high! You're on fire! Don't slow down!",
      },
      {
        time: 550,
        text: "Thirty more seconds of Closeout Chaos! Let's do four more! FRONT! LEFT! BEHIND! RIGHT! Go, go, go!",
      },
      {
        time: 580,
        text: 'Last ten seconds! Close out to the front again! As fast as you can! Go! Chop those feet! Five, four, three, two, one!',
      },
      {
        time: 600,
        text: "Incredible work! Okay, bounce in the middle. Time to be a ball thief! It's time for STEAL THE BACON!",
      },
      {
        time: 610,
        text: 'Get back in your low Superhero Stance! Hands out like shields! Ready to be sneaky?',
      },
      {
        time: 620,
        text: "Imagine the player in front of you is dribbling. We're going to try and poke the ball away! Stay in your stance!",
      },
      {
        time: 630,
        text: "When I say NOW, quickly swipe one hand down at the floor, like you're grabbing bacon! Ready? Right hand... NOW! SWIPE!",
      },
      {
        time: 640,
        text: "And get your hand right back up to your shield position! Don't lean over! Keep your knees bent!",
      },
      {
        time: 650,
        text: 'Okay, left hand ready... NOW! SWIPE! And get that hand right back up!',
      },
      {
        time: 660,
        text: "Great! Now keep shuffling your feet while you wait! Don't just stand there! Shuffle, shuffle, get ready...",
      },
      {
        time: 670,
        text: 'Right hand, NOW! SWIPE! Back to shuffling!',
      },
      {
        time: 680,
        text: 'Left hand, NOW! SWIPE! Back to shuffling!',
      },
      {
        time: 690,
        text: 'Keep your eyes on the imaginary ball! Try to guess when the player will dribble! Be tricky!',
      },
      {
        time: 710,
        text: "Let's do a bunch in a row! Right, Left, Right, Left! Go! SWIPE! SWIPE! SWIPE! SWIPE! ",
      },
      {
        time: 725,
        text: 'Awesome! Keep bouncing on your feet! Thirty more seconds of this! Stay low, stay ready!',
      },
      {
        time: 745,
        text: 'Last few! Three more with each hand! Go! Right! Left! Right! Left! Right! Left!',
      },
      {
        time: 765,
        text: "SUPER-FAST HANDS DRILL! We're going to do ten super-fast swipes! Are you ready? Let's count them together! Go!",
      },
      {
        time: 775,
        text: "One! Two! Three! Four! Five! Six! Seven! Eight! Nine! TEN! You're a pickpocket expert!",
      },
      {
        time: 790,
        text: 'Awesome! Time for another power-up! Stand in the middle and jog in place! Get those knees up! High knees!',
      },
      {
        time: 805,
        text: 'We are recharging our defensive superpowers! Ten seconds left! Go, go, go!',
      },
      {
        time: 820,
        text: "Okay, this is the ultimate challenge! Full-Court Zig-Zag Defense! Let's do it!",
      },
      {
        time: 828,
        text: 'Imagine guarding a player all the way down an imaginary court! We have to shuffle and run backwards in a zig-zag!',
      },
      {
        time: 838,
        text: "Let's try it slow. Shuffle right for three steps... one, two, three...",
      },
      {
        time: 844,
        text: 'NOW, open your hips and run backwards diagonally for three steps... one, two, three...',
      },
      {
        time: 850,
        text: "NOW, turn and shuffle left for three steps! one, two, three! That's the zig-zag!",
      },
      {
        time: 858,
        text: "Okay, you got it? Let's do it for real! Start on one side! Ready? GO! Shuffle, shuffle, shuffle!",
      },
      {
        time: 865,
        text: 'Now turn and run backwards at an angle! Great!',
      },
      {
        time: 872,
        text: "Now face forward and shuffle the other way! Don't cross your feet!",
      },
      {
        time: 880,
        text: "Turn and run backwards again! You're making a giant Z on the floor!",
      },
      {
        time: 890,
        text: "Keep going! Imagine you are staying between your player and the basket! Don't let them beat you!",
      },
      {
        time: 905,
        text: 'Keep your hands up and your feet moving! All the way down the court!',
      },
      {
        time: 920,
        text: "Okay, you made it! Now go back the other way! Don't stop! Shuffle! Run backwards! Shuffle! Run backwards!",
      },
      {
        time: 940,
        text: 'You are a brick wall! Nobody is getting past you! Come on, stay low!',
      },
      {
        time: 960,
        text: "Are your legs getting tired? That's the feeling of getting stronger! Keep zig-zagging!",
      },
      {
        time: 980,
        text: "Check your superhero stance! Are your hands up? Don't get lazy now! This is how champions train!",
      },
      {
        time: 1000,
        text: 'Less than a minute left of this drill! You can do anything for a minute!',
      },
      {
        time: 1020,
        text: "Let's go! One more trip down and back! Make it your best one! Go, go, go!",
      },
      {
        time: 1040,
        text: 'Faster! Push yourself! Final twenty seconds! Shuffle, run, shuffle, run!',
      },
      {
        time: 1060,
        text: 'Ten, nine, eight, seven, six, five, four, three, two, one! AND STOP! Whew!',
      },
      {
        time: 1070,
        text: 'FINAL CHALLENGE! DEFENSIVE FREEZE DANCE! You have ten seconds to get ready! Catch your breath!',
      },
      {
        time: 1080,
        text: "I'll call out moves we learned! Do them super fast! But when I shout FREEZE, you have to stop in a perfect Superhero Stance! Ready? GO! SHUFFLE LEFT!",
      },
      {
        time: 1088,
        text: 'SHUFFLE RIGHT! CLOSEOUT FORWARD! CHOPPY FEET! Now... FREEZE!',
      },
      {
        time: 1095,
        text: "Hold it! Don't move a muscle! Perfect! ...Okay, GO! STEAL THE BACON! SWIPE! ZIG-ZAG BACKWARDS! FREEZE!",
      },
      {
        time: 1105,
        text: "Perfect stance! You're a defensive statue! And... GO! Last ten seconds, fastest feet ever! Any moves you want! Go, go, go, go, go!",
      },
      {
        time: 1115,
        text: 'Five, four, three, two, one... FREEZE!',
      },
      {
        time: 1120,
        text: 'YOU DID IT! AMAZING JOB! You are an official lockdown defender!',
      },
      {
        time: 1128,
        text: 'You completed the Advanced Defense Challenge! Jump up and down! Celebrate! You were awesome today! Great work, superstar!',
      },
    ],
    timeTotal: 1135,
    equipment: 'None',
  },

  // done 1
  {
    id: 2010,
    name: 'Complete Individual Basketball',
    categories: ['Complete', 'advanced'],
    sports: ['basketball'],
    difficulty: 'advanced',
    skill: 'Complete',
    points: 30,
    instructions:
      "Get ready to become a basketball superstar! This is your ultimate training mission. We're going to learn awesome dribbles, super shots, and tricky defense moves to make you the best player on the court! Let's go!",
    timedInstructions: [
      {
        time: 0,
        text: 'Hello basketball hero! Grab your ball and get ready for an adventure!',
      },
      {
        time: 5,
        text: "If you don't have a ball, no problem! Just pretend you're holding one and follow along!",
      },
      {
        time: 10,
        text: "Let's start by waking up our hands! Start dribbling with your right hand. Keep it low, about knee-high!",
      },
      {
        time: 18,
        text: "Use your fingertips, not your palm! Like you're playing a piano on the ball! Tap, tap, tap!",
      },
      {
        time: 25,
        text: 'Keep bouncing on your toes! Bouncy, bouncy, bouncy! Try to look up, not at the ball!',
      },
      {
        time: 33,
        text: "Awesome! Now, pound that ball hard! Like you're making a big drum sound! BOOM, BOOM, BOOM!",
      },
      {
        time: 42,
        text: 'Great power! Back to normal dribbling. Nice and easy. Keep those feet moving!',
      },
      {
        time: 50,
        text: "Three, two, one, SWITCH! Other hand! Left hand now! Let's go!",
      },
      {
        time: 58,
        text: "It might feel a little funny, and that's okay! We're training both sides to be amazing!",
      },
      {
        time: 66,
        text: 'Keep it low! Use those fingertips! Look up and see the world!',
      },
      {
        time: 74,
        text: 'Now, pound it with your left hand! Make those drum sounds! Harder!',
      },
      {
        time: 82,
        text: "Yes! You got it! Back to a regular dribble. Keep breathing! You're doing great!",
      },
      {
        time: 90,
        text: "Okay, get ready for our first move! The Crossover! We're going to switch the ball from one hand to the other right in front of you.",
      },
      {
        time: 98,
        text: "Start dribbling with your right hand. Now, push the ball across to your left hand in a 'V' shape. V for victory!",
      },
      {
        time: 108,
        text: 'Try it! Right to left! Crossover! Now go from left to right! Crossover!',
      },
      {
        time: 116,
        text: 'Keep going! Crossover, crossover! Stay low like a ninja! Shuffle your feet!',
      },
      {
        time: 125,
        text: "Let's do ten in a row! Count with me! One! Two! Three! Four! Five! Six! Seven! Eight! Nine! Ten!",
      },
      {
        time: 138,
        text: 'Amazing! Just bounce the ball with one hand and catch your breath for a second. But keep those feet shuffling!',
      },
      {
        time: 145,
        text: "Time for shooting practice! You don't need a hoop, just imagine one high in the sky!",
      },
      {
        time: 152,
        text: "First, hold the ball in your 'shooting pocket'. That's right in front of your chest, ready to go!",
      },
      {
        time: 160,
        text: "Let's learn the secret code: B-E-E-F! First is B for Balance! Stand with your feet about as wide as your shoulders.",
      },
      {
        time: 170,
        text: 'Next is E for Elbow! Tuck your shooting elbow in, right under the ball, like a little chicken wing.',
      },
      {
        time: 180,
        text: 'Next is another E for Eyes! Look at your imaginary target! Stare right at it!',
      },
      {
        time: 190,
        text: "And last is F for Follow-through! When you shoot, flick your wrist like you're reaching into a cookie jar on a high shelf!",
      },
      {
        time: 200,
        text: "Let's try it! Get in your stance. Balance, Elbow, Eyes... SHOOT! Flick that wrist!",
      },
      {
        time: 210,
        text: 'Again! Get set... and SHOOT! Hold that follow-through pose like a superhero!',
      },
      {
        time: 220,
        text: "Ten practice shots! Let's go! One! Swish! Two! Nothing but net! Three! You're on fire!",
      },
      {
        time: 230,
        text: 'Four! Keep that elbow in! Five! Eyes on the prize! Six! Great flick!',
      },
      {
        time: 240,
        text: 'Seven! Almost there! Eight! Another one! Nine! One more! Ten! Perfect!',
      },
      {
        time: 250,
        text: "Awesome job! Shake your arms out! Let's get back to dribbling! Pick a hand, any hand!",
      },
      {
        time: 258,
        text: 'Time for a tricky move: Between the Legs! Make a triangle with your feet by putting one foot a little forward.',
      },
      {
        time: 268,
        text: 'Now, dribble the ball right through that triangle to your other hand! Whoosh!',
      },
      {
        time: 276,
        text: "Try it a few times! Dribble, dribble, then through the legs! It's okay if you mess up, just grab the ball and try again!",
      },
      {
        time: 286,
        text: 'Right hand, through to the left. Now left hand, through to the right! Keep your body low!',
      },
      {
        time: 296,
        text: "Let's combine them! Get ready! Do a Crossover, then go Between the Legs! Wow!",
      },
      {
        time: 305,
        text: "Try it again! Crossover, then Between the Legs! You're a ball-handling machine!",
      },
      {
        time: 315,
        text: 'Keep practicing that combo! Crossover, Between the Legs! Crossover, Between the Legs! Faster!',
      },
      {
        time: 325,
        text: 'Five, four, three, two, one! And stop! Great work! Just keep a light dribble going.',
      },
      {
        time: 335,
        text: "Now for the coolest move! Behind the Back! It's like the ball is wearing a superhero cape.",
      },
      {
        time: 343,
        text: "You're going to wrap the ball around your body. Dribble on your right side, then give it a little push behind your back to your left hand.",
      },
      {
        time: 353,
        text: "Try it! It's tricky! Go slow at first. It's like you're passing a secret message to your other hand.",
      },
      {
        time: 363,
        text: "Keep those knees bent! Stay low! Let's try to go from left hand to right hand now.",
      },
      {
        time: 373,
        text: "You're getting it! Let's try our super combo now! Crossover, Between the Legs, then Behind the Back!",
      },
      {
        time: 383,
        text: "Whoa! That's a pro move! Let's do it again, nice and slow. Crossover... Between the Legs... Behind the Back!",
      },
      {
        time: 395,
        text: "Keep trying that combo! You're a basketball wizard! Keep shuffling those feet!",
      },
      {
        time: 405,
        text: "And time! Take a second. Keep bouncing lightly on your feet. Don't stop moving!",
      },
      {
        time: 415,
        text: "Now it's time to play defense! No ball needed for this. Put it down for a minute.",
      },
      {
        time: 422,
        text: "Get into your 'Superhero Stance'! Feet wide, knees bent, and get low, low, low!",
      },
      {
        time: 430,
        text: "Put your arms out to the side like you have giant wings! You're a wall that no one can get past!",
      },
      {
        time: 438,
        text: "Now, let's do defensive slides! We shuffle side to side without crossing our feet. Ready? Slide to the right! Go!",
      },
      {
        time: 448,
        text: "Slide, slide, slide! Now back to the left! Slide, slide, slide! Don't let me get by you!",
      },
      {
        time: 458,
        text: 'Stay low! Keep your head up! Imagine a player you have to stop!',
      },
      {
        time: 466,
        text: 'Faster! Right, right, right! Left, left, left! Quick feet, quick feet!',
      },
      {
        time: 475,
        text: 'Ten more seconds of super slides! Come on, you can do it! Stay strong!',
      },
      {
        time: 485,
        text: 'Three, two, one! Freeze! Awesome defense! Shake out your legs. They worked hard!',
      },
      {
        time: 495,
        text: "Grab your ball! Let's put our moves together! We're going to dribble and then shoot!",
      },
      {
        time: 503,
        text: 'Start with one powerful dribble with your right hand, then jump up into your shooting form!',
      },
      {
        time: 511,
        text: 'Dribble, then SHOOT! Remember B-E-E-F! Flick that wrist!',
      },
      {
        time: 520,
        text: 'Now with the left hand! One dribble, then SHOOT! Great job!',
      },
      {
        time: 528,
        text: "Let's get fancier! Do a Crossover, then go right into your shot! Ready? Crossover, SHOOT!",
      },
      {
        time: 538,
        text: "Yes! The defender is so confused! Let's do it again! Crossover, then SHOOT!",
      },
      {
        time: 548,
        text: 'Now try this one. Between the Legs, then SHOOT! Whoa!',
      },
      {
        time: 558,
        text: "Let's do five of those! Between the Legs, SHOOT! That's one! Two! Three! Four! Five! Amazing!",
      },
      {
        time: 570,
        text: "You're a scoring machine! Keep a light dribble going and bounce on your feet. Don't stop moving!",
      },
      {
        time: 580,
        text: "Let's learn a super sneaky move. It's called the 'Hesitation' or the 'Hesi'.",
      },
      {
        time: 588,
        text: "You dribble, then you freeze your body for just a second like you're going to stop. It tricks the defender!",
      },
      {
        time: 596,
        text: 'So, dribble forward, then POP! Stand up a little bit, then get low and explode past them!',
      },
      {
        time: 605,
        text: "Let's try it! Dribble, Hesi, GO! Dribble, Hesi, GO! You're faking everyone out!",
      },
      {
        time: 615,
        text: 'Now add a shot! Dribble, Hesi, then pull up for a SHOT! SWISH!',
      },
      {
        time: 625,
        text: "Let's do that five times! Hesi and shoot! One! Hesi and shoot! Two! Hesi and shoot! Three! Two more! Four! Last one! Five!",
      },
      {
        time: 640,
        text: "Incredible! Keep that light dribble going. You're working so hard!",
      },
      {
        time: 648,
        text: 'Okay, SPEED DRILL CHALLENGE! How many crossovers can you do in 30 seconds? Ready, set, GO!',
      },
      {
        time: 658,
        text: 'Fast hands, fast hands! Low and quick! V-shape, V-shape! Keep counting!',
      },
      {
        time: 668,
        text: "Halfway there! Keep going! Don't stop! Faster, faster, faster!",
      },
      {
        time: 678,
        text: 'Five, four, three, two, one! STOP! How many did you get? Awesome job!',
      },
      {
        time: 685,
        text: 'Catch your breath! But keep your feet shuffling! Always be ready!',
      },
      {
        time: 692,
        text: "Next up, another dribble move called the 'In and Out'. It's a great fake-out.",
      },
      {
        time: 700,
        text: "With your right hand, you push the ball out like you're going to do a crossover, but then you pull it right back! It's one quick motion.",
      },
      {
        time: 710,
        text: 'Out and in! Out and in! With the same hand! Your hand makes a circle around the side of the ball.',
      },
      {
        time: 720,
        text: 'Try it with your left hand now! Out and in! Out and in! Tricky, right?',
      },
      {
        time: 730,
        text: "Let's put that into a combo! Do an In and Out, then a Crossover! WHOA!",
      },
      {
        time: 740,
        text: "Practice that! In and Out, Crossover! In and Out, Crossover! You're unstoppable!",
      },
      {
        time: 750,
        text: "Now let's add a shot to that! In and Out, Crossover, then SHOOT!",
      },
      {
        time: 760,
        text: "Let's do five of those monster combos! That's one! Great shot! Two! Keep it up! Three! Halfway! Four! One more! Five! Perfect!",
      },
      {
        time: 775,
        text: "Amazing! Let's go back to defense! Ball down! Superhero stance, let's go!",
      },
      {
        time: 782,
        text: "This time, when I say 'Go', I want you to do three fast slides, then touch the floor! Ready? GO!",
      },
      {
        time: 790,
        text: 'Slide, slide, slide, TOUCH! Now back the other way! Slide, slide, slide, TOUCH!',
      },
      {
        time: 800,
        text: 'Stay low! Quick feet! This is how you become a defensive champion!',
      },
      {
        time: 810,
        text: 'Keep going! Side to side! Touch the floor! Ten more seconds! Go, go, go!',
      },
      {
        time: 820,
        text: "Five, four, three, two, one! And rest! Shake it out! You're a lightning bolt!",
      },
      {
        time: 830,
        text: "Pick up your ball! Let's do a 'Pound Dribble' challenge. We're going to pound the ball as hard and fast as we can.",
      },
      {
        time: 838,
        text: 'Right hand first! Twenty seconds! As many hard dribbles as you can! Ready? GO!',
      },
      {
        time: 848,
        text: 'Boom, boom, boom! Make the ground shake! Control that power! Look up!',
      },
      {
        time: 858,
        text: 'Three, two, one! Switch hands! Left hand, go! Pound it!',
      },
      {
        time: 868,
        text: 'Come on, power through! You got this! Harder, faster!',
      },
      {
        time: 878,
        text: 'Three, two, one! And stop! Whew! My hands are buzzing! Are yours?',
      },
      {
        time: 885,
        text: "Now we're going to practice another shot. The 'Layup'. This is a shot you take when you're super close to the basket.",
      },
      {
        time: 895,
        text: 'Start on the right side. Take two big steps, right then left, and then lift your right knee as you shoot with your right hand.',
      },
      {
        time: 905,
        text: "It's like skipping! Dribble, then step right, step left, and shoot it up high and soft, off your imaginary backboard.",
      },
      {
        time: 915,
        text: "Let's practice the steps. No ball. Just step right, left, JUMP and reach for the sky!",
      },
      {
        time: 925,
        text: 'Now with the ball! Dribble in, pick it up, step right, left, and LAYUP!',
      },
      {
        time: 935,
        text: "Let's do five on this side! One! Two! Three! Four! Five! Awesome!",
      },
      {
        time: 945,
        text: 'Now the other side! Dribble with your left hand. The steps are opposite! Step left, step right, and shoot with your left hand, lifting your left knee.',
      },
      {
        time: 958,
        text: "Let's practice! Dribble, step left, right, LAYUP! It's okay if this side feels tricky!",
      },
      {
        time: 968,
        text: 'Five layups on the left side! Go! One! Two! Great job! Three! Keep it up! Four! One more! Five! You did it!',
      },
      {
        time: 980,
        text: "You're a scoring superstar! Keep a light dribble going. Let's practice our combos again.",
      },
      {
        time: 988,
        text: "Let's do the ultimate combo! In and Out, Crossover, Between the Legs, then a Hesi, then a SHOT!",
      },
      {
        time: 1000,
        text: "Let's walk through it. In and Out... good. Crossover... nice. Between the Legs... you got it. Hesi to freeze them... and SHOOT for the win!",
      },
      {
        time: 1015,
        text: "Try it three times at your own speed! Go for it! You're a basketball master!",
      },
      {
        time: 1030,
        text: "That looks amazing! Keep practicing that move! The more you do it, the faster you'll get!",
      },
      {
        time: 1040,
        text: 'Okay, stop! Catch your breath. Keep shuffling your feet! Never stand still!',
      },
      {
        time: 1048,
        text: 'SPEED DRILL NUMBER TWO! Between the Legs! How many can you do in 30 seconds? Ready, set, GO!',
      },
      {
        time: 1058,
        text: 'Back and forth! Right through the triangle! Keep your head up as much as you can!',
      },
      {
        time: 1068,
        text: "You're a blur! Keep going, fifteen seconds left! Push it!",
      },
      {
        time: 1078,
        text: "Five, four, three, two, one! STOP! Amazing speed! You're getting so much better!",
      },
      {
        time: 1088,
        text: "Let's go back to shooting form. No ball needed, put it down. Let's make sure our form is perfect.",
      },
      {
        time: 1096,
        text: 'Show me your stance! Balance! Good. Now your elbow! Tucked in! Perfect. Eyes on the target! And... Follow-through! Freeze!',
      },
      {
        time: 1108,
        text: 'Hold that follow-through pose. Are you reaching in the cookie jar? Awesome. Relax.',
      },
      {
        time: 1115,
        text: "Let's do five perfect shots in slow motion. Focus on every single part. One... swish.",
      },
      {
        time: 1125,
        text: 'Two... hold that form. Three... elbow in tight. Four... flick that wrist. Five... picture perfect!',
      },
      {
        time: 1140,
        text: 'Great focus! Grab your ball! We are going to do combo challenges!',
      },
      {
        time: 1147,
        text: 'Challenge one: Two crossovers, then a shot! Go!',
      },
      {
        time: 1155,
        text: 'Do it again! Two crossovers, shot! Nice!',
      },
      {
        time: 1162,
        text: 'Challenge two: Two between the legs, then a layup! Go for it!',
      },
      {
        time: 1170,
        text: 'Again! Dribble, dribble through the legs, then two steps and a layup!',
      },
      {
        time: 1178,
        text: 'Challenge three: In and out dribble, hesitation, then a jump shot! Go!',
      },
      {
        time: 1186,
        text: "That's a super tricky one! Try it again! In and Out, Hesi, SHOT!",
      },
      {
        time: 1195,
        text: "You are crushing these challenges! Let's do a 'Figure Eight' dribble to work on control.",
      },
      {
        time: 1203,
        text: 'Stand with your feet wide. Dribble the ball in a figure eight pattern around and through your legs.',
      },
      {
        time: 1211,
        text: 'Stay low! Use your fingertips to guide the ball. Go one way... then try to go the other way!',
      },
      {
        time: 1220,
        text: "This one is tough! It's okay if you lose the ball. Just keep trying! This makes your handles super strong!",
      },
      {
        time: 1230,
        text: 'Keep going! Ten more seconds of Figure Eights! You can do it!',
      },
      {
        time: 1240,
        text: 'And stop! Fantastic effort! Just keep a light dribble going with one hand. Keep bouncing!',
      },
      {
        time: 1248,
        text: "Let's go back to defense for one last blast! Ball down! Superhero stance!",
      },
      {
        time: 1255,
        text: 'We are going to do super fast feet! Stay in your stance and chop your feet as fast as you can! Go!',
      },
      {
        time: 1263,
        text: "Like you're running in place but you're low and wide! Pitter-patter, pitter-patter! Faster!",
      },
      {
        time: 1272,
        text: "Now, while you do that, I'm going to call out 'Left' or 'Right'! You do one quick slide in that direction and then go back to fast feet! Ready?",
      },
      {
        time: 1282,
        text: 'Fast feet, fast feet... RIGHT! Slide! Back to fast feet!',
      },
      {
        time: 1290,
        text: 'Keep chopping! Keep chopping! LEFT! Slide! Back to fast feet!',
      },
      {
        time: 1298,
        text: 'RIGHT! Slide! Go, go, go! LEFT! Slide! Yes! RIGHT! Slide! Amazing reaction time!',
      },
      {
        time: 1310,
        text: 'Five more seconds of just fast feet! As fast as you can! Go, go, go, go, go!',
      },
      {
        time: 1315,
        text: 'And STOP! You are a defensive wall! Shake out those legs!',
      },
      {
        time: 1322,
        text: "Okay, grab your ball. We're getting near the end. Let's review our favorite moves.",
      },
      {
        time: 1330,
        text: 'Show me your best Crossover! Make it sharp!',
      },
      {
        time: 1337,
        text: 'Now show me your best Between the Legs! Low and fast!',
      },
      {
        time: 1344,
        text: 'Show me your best Behind the Back! Make it smooth!',
      },
      {
        time: 1351,
        text: 'And show me your best jump shot! Perfect B-E-E-F form!',
      },
      {
        time: 1360,
        text: "You've learned so much! Let's do a freestyle combo round. I'll give you 30 seconds to mix up any moves you want!",
      },
      {
        time: 1370,
        text: 'Dribble, shoot, do whatever you want! Show me what you got! Ready? Go!',
      },
      {
        time: 1378,
        text: 'Try a crossover into a between the legs! Or a hesi into a layup! Be creative!',
      },
      {
        time: 1388,
        text: 'Ten seconds left! Show me your flashiest move! Go for it!',
      },
      {
        time: 1400,
        text: 'Three, two, one! Awesome freestyle! You looked like a pro!',
      },
      {
        time: 1408,
        text: "Let's do another speed drill! This time, it's 'Low Dribbles'. I want you to dribble the ball as low and fast as you can, just above your shoe!",
      },
      {
        time: 1418,
        text: 'Twenty seconds with the right hand! Get low! Go!',
      },
      {
        time: 1425,
        text: 'Tap, tap, tap, tap! Super fast fingertips! Like a machine gun!',
      },
      {
        time: 1438,
        text: 'Switch hands! Left hand! Go! Low and fast!',
      },
      {
        time: 1445,
        text: 'Keep it under control! You got it! Faster!',
      },
      {
        time: 1455,
        text: 'And stop! Incredible ball control!',
      },
      {
        time: 1460,
        text: 'We are going to put everything together for one final super challenge.',
      },
      {
        time: 1467,
        text: "The challenge is called 'The Gauntlet'. I'm going to call out a move, you do it, then take one jump shot.",
      },
      {
        time: 1475,
        text: 'Ready? First up: Crossover, then shoot! Go!',
      },
      {
        time: 1482,
        text: 'Nice! Get your ball. Next: Between the legs, then shoot! Go!',
      },
      {
        time: 1490,
        text: 'Great! Next: Behind the back, then shoot! Go!',
      },
      {
        time: 1498,
        text: 'Awesome! Next: Hesitation move, then shoot! Go!',
      },
      {
        time: 1506,
        text: 'You got it! Next: In and Out, then shoot! Go!',
      },
      {
        time: 1514,
        text: 'Final one! Your choice! Any dribble move you want, then shoot! Go!',
      },
      {
        time: 1525,
        text: 'You passed The Gauntlet! You are a true basketball champion!',
      },
      {
        time: 1532,
        text: "For our last little bit, we're going to practice keeping our dribble alive while we're tired.",
      },
      {
        time: 1540,
        text: "Just start a nice, easy dribble. We're going to keep this dribble going until the very end.",
      },
      {
        time: 1548,
        text: "Switch hands whenever you want. Walk around your room a little bit. Just don't let the dribble stop.",
      },
      {
        time: 1558,
        text: 'Keep bouncing on your feet. Think about all the moves you learned today.',
      },
      {
        time: 1568,
        text: 'You learned crossovers, between the legs, behind the back, hesitations, and in-and-outs!',
      },
      {
        time: 1578,
        text: 'You learned how to shoot with perfect form and how to do a layup! And you played amazing defense!',
      },
      {
        time: 1588,
        text: 'Keep that ball bouncing. We have less than a minute left. You can do it.',
      },
      {
        time: 1598,
        text: 'Try a slow-motion crossover. Now a slow-motion between the legs. Feel the ball.',
      },
      {
        time: 1608,
        text: "Thirty seconds left! Let's do a countdown! Keep dribbling!",
      },
      {
        time: 1618,
        text: 'Here we go! Twenty! Nineteen! Eighteen! Seventeen! Sixteen!',
      },
      {
        time: 1628,
        text: 'Fifteen! Fourteen! Thirteen! Twelve! Eleven!',
      },
      {
        time: 1638,
        text: "Final ten! Let's pound it hard to the finish line! TEN! NINE! EIGHT! SEVEN! SIX!",
      },
      {
        time: 1648,
        text: 'FIVE! FOUR! THREE! TWO! ONE! STOP!',
      },
      {
        time: 1655,
        text: 'You did it! You completed the entire basketball mission! That was amazing!',
      },
      {
        time: 1662,
        text: 'You worked so hard and learned so much! You are a basketball superstar!',
      },
      {
        time: 1668,
        text: 'Great job today! See you next time for another adventure!',
      },
    ],
    timeTotal: 1674,
    equipment: 'Basketball and hoop (optional)',
  },
];

export default basketballDrills;
